import {
  CaffeineBreakdown,
  DietPreference,
  Ethnicity,
  FeedbackType,
  Gender,
  GeneDataGroupArea,
  GeneDataGroupType,
  Intolerance,
  LoginError,
  PasswordResetError,
  RegisterError,
  ResilienceLevel,
  VerifyCodeError,
} from "../api/smartmelts-api/graphql/generated";
import { MenuItemCategory, MenuItemSubcategory } from "../models/types/category-types";
import { UpdatePasswordError } from "./../api/smartmelts-api/graphql/generated";
import { nutrientSourcesDE } from "./de/nutrient-sources";

const nutrientTranslations = {
  B_CAROTENE: {
    name: "ß-Carotin",
    info:
      "Diese Vitamine und Pflanzenstoffe sind vor allem in farbigem Gemüse enthalten und wirken sich besonders bei bestimmten genetischen Typen positiv auf die Entgiftung, den Schutz vor freien Radikalen und die kognitive Gesundheit aus.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Unterstützt die Genexpression",
      [GeneDataGroupType.EYE]: "Unterstützt die Augengesundheit",
      [GeneDataGroupType.BRAIN]: "Schützt vor geistigem Verfall",
      [GeneDataGroupType.JOINT]: "Hilft bei arthritischen Schmerzen",
      [GeneDataGroupType.METABOLISM]: "Unterstützt das Immunsystem",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Schützt vor photooxidativen Schäden",
      [GeneDataGroupType.GLUTEN]: "Unterstützt das Immunsystem",
    },
  },
  FOLIC_ACID: {
    name: "Folsäure",
    info:
      "Diese Vitamine helfen in der Regulierung des Homozysteinstoffwechsels, einem durch Genvariationen beeinflussten Blutwert, der für eine optimale Herzgesundheit unter Kontrolle gehalten werden sollte.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Essentiell für die gesunde Gehirnfunktion",
      [GeneDataGroupType.BRAIN]: "Unterstützt die Produktion von DNA und RNA",
      [GeneDataGroupType.HOMOCYSTEINE]: "Wird zur Energiegewinnung verwendet",
    },
  },

  VIT_A: {
    name: "Vitamin A",
    info:
      "Diese Vitamine und Pflanzenstoffe sind vor allem in farbigem Gemüse enthalten und wirken sich besonders bei bestimmten genetischen Typen positiv auf die Entgiftung, den Schutz vor freien Radikalen und die kognitive Gesundheit aus.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Genexpression und Zelldifferenzierung",
    },
  },
  VIT_B2: {
    name: "Vitamin B2",
    info:
      "Ähnlich wie Folsäure und Vitamin B6 und B12, trägt Vitamin B2 zur Stabilisierung des Homozysteinstoffwechsels bei, aber nur wenn eine bestimmte Genvariation vorliegt. Ist diese Genvariation nicht vorhanden, zeigt Vitamin B2 keine Auswirkung auf den Homozysteinspiegel.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Wandelt Kohlenhydrate in Glukose um",
      [GeneDataGroupType.BRAIN]: "Reduziert Nervenentzündungen",
      [GeneDataGroupType.HOMOCYSTEINE_B2]: "Reduziert den Homocysteinspiegel",
      [GeneDataGroupType.HYPERTENSION]: "Senkt den Blutdruck",
      [GeneDataGroupType.METABOLISM]: "Trägt zum Eisenstoffwechsel bei",
      [GeneDataGroupType.GLUTEN]: "Wandelt Kohlenhydrate in Glukose um",
    },
  },
  VIT_B6: {
    name: "Vitamin B6/B12",
    info:
      "Diese Vitamine helfen in der Regulierung des Homozysteinstoffwechsels, einem durch Genvariationen beeinflussten Blutwert, der für eine optimale Herzgesundheit unter Kontrolle gehalten werden sollte.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Behält die normale Nervenfunktion bei",
      [GeneDataGroupType.BRAIN]: "Hilft bei der Bildung von Neurotransmittern",
      [GeneDataGroupType.HOMOCYSTEINE]: "Senkt den Homocysteinspiegel",
      [GeneDataGroupType.METABOLISM]: "Unterstützt den Kohlenhydrat-, Eiweiß- und Fettstoffwechsel",
      [GeneDataGroupType.GLUTEN]: "Behält die normale Nervenfunktion bei",
    },
  },
  VIT_C: {
    name: "Vitamin C",
    info:
      "Diese Stoffe sind starke Antioxidanzien und wirken sich besonders bei bestimmten genetischen Typen positiv auf die kognitive Gesundheit, die Augengesundheit, die Gelenksgesundheit und den Oxidativen Stress aus.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Unterstützt Wachstum und Gewebereparatur",
      [GeneDataGroupType.BONE]: "Unverzichtbar für die Kollagenbildung",
      [GeneDataGroupType.EYE]: "Schützt vor grauem Star",
      [GeneDataGroupType.BRAIN]: "Reguliert die Neurotransmittersynthese",
      [GeneDataGroupType.METABOLISM]: "Unterstützt den Eiweißstoffwechsel",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Reduziert Schäden durch freie Radikale",
      [GeneDataGroupType.GLUTEN]: "Unterstützt Wachstum und Gewebereparatur",
    },
  },
  VIT_D3: {
    name: "Vitamin D",
    info:
      "Dieses Vitamin wird bei Sonnenlicht von der Haut selbst produziert und ist ein wichtiger Faktor für gesunde Knochen. Bestimmte genetische Typen benötigen höhere Mengen dieses Vitamins, um die Gesundheit der Knochen zu erhalten.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Hält normale Kalzium- und Phosphorwerte im Blut aufrecht",
      [GeneDataGroupType.BONE]: "Hilft bei der Absorption von Kalzium und Phosphor",
      [GeneDataGroupType.BRAIN]: "Schützt die Gehirnfunktion",
      [GeneDataGroupType.METABOLISM]: "Unterstützt den Eiweißstoffwechsel",
      [GeneDataGroupType.GLUTEN]: "Unterstützt das Immunsystem",
      [GeneDataGroupType.LACTOSE]: "Hält den normalen Kalziumspiegel im Blut aufrecht",
    },
  },
  VIT_E: {
    name: "Vitamin E",
    info:
      "Diese Stoffe sind starke Antioxidanzien und wirken sich besonders bei bestimmten genetischen Typen positiv auf die kognitive Gesundheit, die Augengesundheit, die Gelenksgesundheit und den Oxidativen Stress aus.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Wirkt als Antioxidans",
      [GeneDataGroupType.EYE]: "Schützt die Augen vor freien Radikalen",
      [GeneDataGroupType.BRAIN]: "Erhält die Gesundheit des Gehirns",
      [GeneDataGroupType.METABOLISM]: "Wirkt in durch freie Radikale geschädigten Zellen",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Dämpft oxidative Schäden",
      [GeneDataGroupType.GLUTEN]: "Wirkt als Antioxidans",
    },
  },
  IRON: {
    name: "Eisen",
    info:
      "Eisen ist ein wichtiger Bestandteil des Blutes, das als Hämoglobin für den Transport von Sauerstoff durch den Körper verantwortlich ist. Wird Eisen jedoch durch bestimmte Genvariationen in zu großer Menge aus der Nahrung aufgenommen, kann dies die Organe schädigen.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Hilft Sauerstoff durch den Körper zu transportieren",
      [GeneDataGroupType.METABOLISM]: "Hilft Sauerstoff durch den Körper zu transportieren",
      [GeneDataGroupType.DETOX]: "Hilft Giftstoffe abzubauen und zu entfernen",
      [GeneDataGroupType.GLUTEN]: "Hilft Sauerstoff durch den Körper zu transportieren",
    },
  },
  POTASSIUM: {
    name: "Kalium",
    info:
      "Kalium ist ein wichtiger Nährstoff für eine Vielzahl von Körperfunktionen, hat aber auch eine besondere Bedeutung für den Blutdruck und die kognitive Gesundheit. Bestimmte genetische Typen benötigen daher eine höhere Zufuhr von Kalium.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Unterstützt einen regelmäßigen Herzschlag (Sinusryhtmus)",
      [GeneDataGroupType.BRAIN]: "Hilft bei der Kommunikation der Gehirnzellen",
    },
  },
  CALCIUM: {
    name: "Kalzium",
    info:
      "Kalzium ist ein wichtiger Bestandteil vieler Körperfunktionen. Bestimmte genetische Typen benötigen eine höhere Zufuhr von Kalzium, um die Knochengesundheit zu erhalten und die Entgiftung zu unterstützen.",
    functions: {
      [GeneDataGroupType.TOTAL]:
        "Wichtig für die Gesundheit von Knochen und Zähnen. Beteiligt an Blutgerinnung, Muskelkontraktion und Nervenfunktion",
      [GeneDataGroupType.BONE]: "Baut Kohlenhydrate ab",
      [GeneDataGroupType.METABOLISM]: "Unterstützt den Zellstoffwechsel",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Stimuliert Antioxidantien",
      [GeneDataGroupType.DETOX]: "Unterstützt die Toxinabwehr",
      [GeneDataGroupType.GLUTEN]: "Wichtig für die Nervenfunktion",
      [GeneDataGroupType.LACTOSE]: "Wichtig für die Gesundheit von Knochen und Zähnen",
    },
  },
  COPPER: {
    name: "Kupfer",
    info:
      "Kupfer ist ein wichtiger Bestandteil vieler Enzyme und ist deshalb ein für den Stoffwechsel wichtiges Spurenelement. Zusätzlich reduziert Kupfer die Aggressivität des Immunsystems und den Verlauf der Makuladegeneration und ist deshalb bei bestimmten genetischen Typen besonders wichtig.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Hilft gesunde Knochen, Blutgefäße, Nerven und die Immunfunktion zu erhalten",
      [GeneDataGroupType.EYE]: "Verbessert antioxidative Enzymfunktionen",
      [GeneDataGroupType.METABOLISM]: "Unterstützt den Energiestoffwechsel und die Entgiftung",
      [GeneDataGroupType.GLUTEN]: "Hilft den Nerven und der Immunfunktion",
    },
  },
  MAGNESIUM: {
    name: "Magnesium",
    info:
      "Magnesium ist ein wichtiger Bestandteil von über 300 Enzymen und hat deshalb für den Stoffwechsel, die Funktion der Muskelzellen sowie auch die Knochengesundheit große Bedeutung.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Unterstützt die Muskel- und Nervenfunktion",
    },
  },
  MANGANESE: {
    name: "Mangan",
    info:
      "Mangan ist wichtiger Bestandteil vieler Enzyme und auf diese Weise bei der Neutralisierung von freien Radikalen sowie der Gesundheit der Gelenke involviert. Bestimmte genetische Typen benötigen deshalb größere Mengen an Mangan um ihre Körperfunktionen zu unterstützen.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Unterstützt die Produktion von kognitivem Gewebe, Knochen und Sexualhormonen",
      [GeneDataGroupType.BRAIN]: "Notwendig für die Gehirn- und Nervenfunktion",
      [GeneDataGroupType.METABOLISM]: "Aktiviert Enzyme",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Reduziert oxidativen Stress",
      [GeneDataGroupType.GLUTEN]: "Notwendig für die Gehirn- und Nervenfunktion",
    },
  },
  SODIUM: {
    name: "Natrium",
    info:
      "Natrium ist ein Bestandteil des Kochsalzes und führt bei manchen Menschen zu einem ungesunden Anstieg des Blutdruckes.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Unterstützt den Elektrolythaushalt des Körpers",
      [GeneDataGroupType.BONE]: "Eine übermäßige Einnahme kann zu Osteoporose führen",
      [GeneDataGroupType.BRAIN]: "Beeinflusst die Übertragung von Nervenimpulsen",
      [GeneDataGroupType.HYPERTENSION]: "Beeinflusst den pH-Wert und den Blutdruck",
      [GeneDataGroupType.METABOLISM]: "Behält die normale zelluläre Homöostase bei",
    },
  },
  PHOSPHORUS: {
    name: "Phosphor",
    info:
      "Diese Stoffe sind Bestandteil vieler Nahrungsmittel und können sich bei bestimmten genetischen Typen negativ auf die Knochengesundheit auswirken.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Beeinflusst den Energieverbrauch und -speicherung im Körper",
      [GeneDataGroupType.BONE]: "Ein Überschuss kann zu Knochenschwund führen",
    },
  },
  SELENE: {
    name: "Selen",
    info:
      "Selen ist ein Bestandteil vieler Enzyme, die unter anderem freie Radikale neutralisieren. Bestimmte genetische Typen benötigen höhere Mengen an Selen, um den Schutz vor freien Radikalen zu unterstützen.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Beteiligt an Fortpflanzung, Hormonen und DNA-Synthese",
      [GeneDataGroupType.BRAIN]: "Verhindert geistigen Verfall und Gedächtnisverlust",
      [GeneDataGroupType.METABOLISM]: "Hilft bei der Produktion von Schilddrüsenhormonen",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Reduziert oxidativen Stress",
      [GeneDataGroupType.DETOX]: "Reduziert oxidativen Stress",
      [GeneDataGroupType.GLUTEN]: "Hilft bei der Produktion von Schilddrüsenhormonen",
    },
  },
  ZINC: {
    name: "Zink",
    info:
      "Zink wird vom Körper benötigt, um Makronährstoffe richtig zu verstoffwechseln, Proteine richtig aufbauen zu können, die Knochen gesund zu halten und neue DNA herstellen zu können. Als essenzieller Bestandteil vieler Proteine und Enzyme ist es ein lebenswichtiger Mikronährstoff.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Unterstützt das Immunsystem",
      [GeneDataGroupType.EYE]: "Strukturiert das Sehpigment der Netzhaut",
      [GeneDataGroupType.BRAIN]: "Beteiligt an der Übertragung von Nervenimpulsen",
      [GeneDataGroupType.METABOLISM]:
        "Ermöglicht den Abbau und die Synthese von Lipiden, Proteinen, Kohlenhydraten und Nukleinsäuren",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Beteiligt am Proteinstoffwechsel",
      [GeneDataGroupType.DETOX]: "Schützt vor Antioxidantien",
      [GeneDataGroupType.GLUTEN]: "Unterstützt das Immunsystem",
    },
  },
  PHYTIC_ACID: {
    name: "Phytinsäure",
    info:
      "Diese Stoffe sind Bestandteil vieler Nahrungsmittel und können sich bei bestimmten genetischen Typen negativ auf die Knochengesundheit auswirken.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Hemmt die Bioverfügbarkeit von Mikronährstoffen",
      [GeneDataGroupType.BONE]: "Reduziert das Risiko von Knochenbrüchen",
    },
  },
  FIBER: {
    name: "Ballaststoffe",
    info:
      "Ballaststoffe sind weitgehend unverdauliche Nahrungsbestandteile, die vorwiegend in pflanzlichen Lebensmitteln vorkommen. Als Massebestandteil der Nahrung helfen sie dem Darm bei der Verdauung.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Hilft den Zuckerverbrauch des Körpers zu regulieren",
      [GeneDataGroupType.HEART]: "Hilft Herzkrankheiten vorzubeugen",
      [GeneDataGroupType.BRAIN]: "Hilft die Alterung des Gehirns zu verzögern",
      [GeneDataGroupType.METABOLISM]: "Hält Hunger und Blutzucker in Schach",
      [GeneDataGroupType.GLUTEN]: "Hilft den Zuckerverbrauch des Körpers zu regulieren",
    },
  },
  KOFFEINE: {
    name: "Koffein",
    info:
      "Kaffee enthält eine sehr hohe Menge an Antioxidantien und bietet deshalb für diverse Stoffwechselprobleme und Krankheiten, die mit der Entstehung von freien Radikalen zu tun haben, einen erheblichen Schutz. Das im Kaffee enthaltene Koffein kann sich jedoch negativ auf die Knochengesundheit auswirken und sollte bei bestimmten genetischen Typen gemieden werden.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Stimuliert das zentrale Nervensystem sowie Herz, Muskeln und Blutdruck",
      [GeneDataGroupType.HEART]: "Stimuliert die Herzkontraktionsintensität",
      [GeneDataGroupType.BONE]: "Verringert die Knochenmineraldichte",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Reduziert oxidativen Stress",
    },
  },
  LUTEINE: {
    name: "Lutein",
    info:
      "Lutein und Lykopen sind Pflanzenfarbstoffe der Gruppe der Carotinoide und haben gezeigt, dass sie sich positiv auf die Augengesundheit auswirken. Deshalb sind sie besonders für bestimmte genetische Typen förderlich und sollten erhöht werden.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Schützt das Augengewebe vor Sonnenlicht",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Reduziert oxidativen Stress",
      [GeneDataGroupType.DETOX]: "Hilft bei der Bekämpfung von Schäden durch freie Radikale",
    },
  },
  LYCOPENE: {
    name: "Lycopene",
    info:
      "Lutein und Lykopen sind Pflanzenfarbstoffe der Gruppe der Carotinoide und haben gezeigt, dass sie sich positiv auf die Augengesundheit auswirken. Deshalb sind sie besonders für bestimmte genetische Typen förderlich und sollten erhöht werden.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Bietet Schutz vor freien Radikalen",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Bietet Schutz vor freien Radikalen",
      [GeneDataGroupType.DETOX]: "Kann Schäden durch Toxine verhindern",
    },
  },
  MSM: {
    name: "Methylsulfonylmethan",
    info:
      "Diese organische Schwefelverbindung wirkt entzündungshemmend und kann bestimmten genetischen Typen helfen, die Gelenke vor einem zu aggressiven Immunsystem zu beschützen.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Verringert Entzündungen",
      [GeneDataGroupType.JOINT]: "Reduziert Gelenkschmerzen",
    },
  },
  OXALIC_ACID: {
    name: "Oxalsäure",
    info:
      "Diese Stoffe sind Bestandteil vieler Nahrungsmittel und können sich bei bestimmten genetischen Typen negativ auf die Knochengesundheit auswirken.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Bindet mit Mineralien",
      [GeneDataGroupType.BONE]: "Bindet mit Kalzium",
    },
  },
  PHYTOSTEROL: {
    name: "Phytosterol",
    info:
      "Für genetische Typen, bei denen sich Omega-3-Fettsäuren negativ auf den Cholesterinspiegel auswirken, sind Phytosterole eine gute Alternative, um das HDL Cholesterin zu verbessern.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Blockiert die Cholesterinaufnahme",
      [GeneDataGroupType.HEART]: "Verbessert die Herzgesundheit",
    },
  },
  A_LINOLENIC_ACID: {
    name: "Alpha Linolensäure",
    info:
      "Alpha Linolensäure ist für die Umwandlung von Omega 3 Fettsäuren notwendig und hat positive Auswirkung auf die Gelenksgesundheit. Andererseits hat diese Fettsäure negative Auswirkungen auf die Augengesundheit bestimmter genetischer Typen.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Notwendig für normales Wachstum und Entwicklung",
      [GeneDataGroupType.HEART]: "Verbessert die Herzgesundheit",
      [GeneDataGroupType.EYE]: "Fördert die Gesundheit der Augen",
      [GeneDataGroupType.JOINT]: "Reduziert Entzündungen",
    },
  },
  OMEGA3: {
    name: "Omega-3-Fettsäuren",
    info:
      "Dieser Überbegriff beschreibt sämtliche Omega-3-Fettsäuren, die besonders in Fisch und Fischöl sowie bestimmten Pflanzen enthalten sind. Diese wirken sich positiv auf die Gelenksgesundheit aus, können aber je nach Genen die HDL-Cholesterinwerte verschlechtern oder verbessern.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Reguliert Blutgerinnung, Arterienwände und Entzündungen",
    },
  },
  DHA: {
    name: "Docosahexaensäure (DHA)",
    info:
      "Diese Omega-3-Fettsäuren sind für die Normalisierung der Cholesterinwerte und die richtige Funktion des Immunsystems, der Gelenksgesundheit und der kognitiven Gesundheit wichtig. Bei bestimmten genetischen Typen führen sie jedoch zu einer Verschlechterung des HDL Cholesterinspiegels und sollten reduziert werden.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Schlüssel für die Entwicklung von Augen- und Nervengewebe",
      [GeneDataGroupType.HEART]: "Erhöht das gute Cholesterin (HDL)",
      [GeneDataGroupType.EYE]: "Verbessert das Sehvermögen",
      [GeneDataGroupType.BRAIN]: "Verbessert die Gehirnfunktion",
      [GeneDataGroupType.JOINT]: "Hilft Entzündungen zu reduzieren",
      [GeneDataGroupType.METABOLISM]: "Unterstützt die Zellsignalisierung und -kommunikation",
      [GeneDataGroupType.GLUTEN]: "Erhöht das gute Cholesterin (HDL)",
    },
  },
  EPA: {
    name: "Eicosapentaensäure (EPA)",
    info:
      "Diese Omega-3-Fettsäuren sind für die Normalisierung der Cholesterinwerte und die richtige Funktion des Immunsystems, der Gelenksgesundheit und der kognitiven Gesundheit wichtig. Bei bestimmten genetischen Typen führen sie jedoch zu einer Verschlechterung des HDL Cholesterinspiegels und sollten reduziert werden.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Verhindert die Blutgerinnung und senkt den Triglyceridspiegel im Blut",
      [GeneDataGroupType.HEART]: "Hilft das Risiko von Herzerkrankungen zu senken",
      [GeneDataGroupType.EYE]: "Wohltuend bei trockenen Augen",
      [GeneDataGroupType.BRAIN]: "Unterstützt Lernen, Gedächtnis und synoptische Plastizität",
      [GeneDataGroupType.JOINT]: "Hilft Entzündungen zu reduzieren",
      [GeneDataGroupType.METABOLISM]: "Hilfreich gegen Fettleibigkeit",
      [GeneDataGroupType.GLUTEN]: "Reduziert den Triglyceridspiegel im Blut",
    },
  },
  SAT_FAT: {
    name: "Gesättigte Fettsäuren",
    info:
      "Gesättigte Fettsäuren wirken sich bei zu hoher Aufnahme negativ auf die Herzgesundheit aus. Bestimmte genetische Typen reagieren besonders empfindlich auf die Menge an gesättigten Fettsäuren und sollten den Verzehr reduzieren.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Am Cholesterinstoffwechsel beteiligt",
      [GeneDataGroupType.HEART]: "Erhöht den LDL-Spiegel (schlechtes Cholesterin)",
      [GeneDataGroupType.EYE]: "Kann Plaquebildung verursachen",
      [GeneDataGroupType.BRAIN]: "Kann das Sättigungssignal stören",
      [GeneDataGroupType.JOINT]: "Schwächt das Knorpelgewebe",
      [GeneDataGroupType.HYPERTENSION]: "Erhöht den Cholesterinspiegel im Blut",
      [GeneDataGroupType.METABOLISM]: "Verlangsamt den Stoffwechsel",
      [GeneDataGroupType.GLUTEN]: "Erhöht den LDL-Spiegel (schlechtes Cholesterin)",
    },
  },
  UNSAT_FAT: {
    name: "Ungesättigte Fettsäuren",
    info: 'Ungesättigten Fettsäuren werden generell als das "gesunde Fett" bezeichnet.',
    functions: {
      [GeneDataGroupType.TOTAL]: "Verbessert den Cholesterinspiegel im Blut",
      [GeneDataGroupType.HEART]: "Stabilisiert den Herzrhythmus",
      [GeneDataGroupType.BRAIN]: "Wichtig für Lernen und Gedächtnis",
      [GeneDataGroupType.JOINT]: "Reduziert Entzündungsschmerzen",
      [GeneDataGroupType.METABOLISM]: "Hilft die Fluidität der Zellmembran aufrechtzuerhalten",
      [GeneDataGroupType.GLUTEN]: "Verbessert den Cholesterinspiegel im Blut",
    },
  },
  ARACHIDONIC_ACID: {
    name: "Arachidonsäure",
    info:
      "Arachidonsäure gilt als Botenstoff im Körper, der negative Reaktionen auslöst und besonders für genetisch veranlagte Personen besonders ungesund ist und gemieden werden sollte.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Unterstützt die Zellflüssigkeit und schützt vor oxidativem Stress",
      [GeneDataGroupType.JOINT]: "Reduziert Entzündungen",
    },
  },
  CHOLESTEROL: {
    name: "Cholesterin",
    info:
      "Cholesterin ist eine Substanz, die der menschliche Körper selbst produzieren kann, zum Teil aber auch durch die Nahrung aufnimmt und im Überschuss eine Reihe von negativen Auswirkungen auf den Körper hat.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Erhält die Zellintegrität (Lyse) und Fließfähigkeit",
      [GeneDataGroupType.HEART]: "Eine übermäßige Aufnahme kann sich an Arterienwänden ansammeln",
    },
  },
  FRUCTOSE: {
    name: "Fruktose",
    info:
      "Fruktose ist der sogenannte Fruchtzucker und muss vom Körper nicht erst in kleinere Bestandteile umgewandelt werden, bevor er aufgenommen wird. Manche Menschen sind unverträglich auf Fruktose (sog. Fruktose-Intoleranz) und sollten genau so wie bestimmte genetische Typen die übermäßige Fruktose-Zufuhr meiden.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Verbessert den Geschmackssinn",
      [GeneDataGroupType.HEART]: "Eine übermäßige Aufnahme erhöht das Risiko für Herzerkrankungen",
      [GeneDataGroupType.BRAIN]: "Schädigt die Kommunikation zwischen Gehirnzellen",
      [GeneDataGroupType.JOINT]: "Verstärkt Entzündungsprozesse im Körper",
      [GeneDataGroupType.METABOLISM]: "Erzeugt schädliche metabolische Wirkungen",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Provoziert oxidativen Stress in der Großhirnrinde",
    },
  },
  SUGAR: {
    name: "Zucker",
    info:
      "Diese zwei hauptsächlichen Energielieferanten sind Bestandteil der meisten Nahrungsmittel und können bei zu hoher Zufuhr zu Übergewicht und Stoffwechselproblemen führen. Bestimmte genetische Typen sind besonders empfindlich auf die negativen Effekte von zu viel Fett oder Zucker und sollten den Konsum einschränken.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Liefert Energie",
      [GeneDataGroupType.HEART]: "Übermäßige Einnahme wirkt sich auf die Arterien aus",
      [GeneDataGroupType.BRAIN]: "Eine übermäßige Aufnahme beeinträchtigt die kognitiven Fähigkeiten",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Erhöht den Insulin- und Glukosespiegel",
      [GeneDataGroupType.METABOLISM]: "Kann eine Dysregulation des Stoffwechsels verursachen",
    },
  },
  LACTOSE: {
    name: "Laktose",
    info:
      "Laktose ist der sogenannte Milchzucker, der von manchen Menschen nicht gut vertragen wird. Je nach Genetik spricht der Körper unterschiedlich gut auf Laktose an und sollte bei manchen Menschen reduziert werden.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Wird vom Körper als Energiequelle verwendet",
      [GeneDataGroupType.GLUTEN]: "Wird vom Körper als Energiequelle verwendet",
      [GeneDataGroupType.LACTOSE]: "Wird vom Körper als Energiequelle verwendet",
    },
  },
  SUCROSE: {
    name: "Saccharose",
    info:
      "Dieser Zucker wirkt sich bei bestimmten genetischen Typen besonders negativ auf die kognitive Gesundheit und den Blutzuckerspiegel aus.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Liefert Energie",
      [GeneDataGroupType.BRAIN]: "Übermäßige Einnahme beeinflusst das Gedächtnis und die kognitive Funktion",
      [GeneDataGroupType.METABOLISM]: "Kann die Insulinsensitivität und den Fettstoffwechsel beeinflussen",
    },
  },
  ALCOHOL: {
    name: "Alkohol",
    info:
      "Alkohol ist ein Genussmittel, das bei zu hohem Konsum zu einer Vielzahl von gesundheitlichen Problemen, aber auch zur Abhängigkeit führen kann. Aufgrund individueller genetischer Unterschiede wirkt sich Alkohol unterschiedlich auf verschiedene Personen aus.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Beeinflusst Stimmung, Verhalten, Koordination und Konzentration",
      [GeneDataGroupType.HEART]: "Erhöht vorübergehend die Herzfrequenz und Druck",
      [GeneDataGroupType.BONE]: "Beeinflusst die Knochendichte",
      [GeneDataGroupType.JOINT]: "Verstärkt Entzündungsprozesse im Körper",
      [GeneDataGroupType.BRAIN]: "Beeinträchtigt die Gehirnfunktion",
      [GeneDataGroupType.HOMOCYSTEINE]: "Erhöhtes Homocystein erhöht das Risiko einer Venenthrombose",
      [GeneDataGroupType.HYPERTENSION]: "Erhöht den Blutdruck",
      [GeneDataGroupType.METABOLISM]: "Verlangsamt den Stoffwechsel des Körpers",
    },
  },
  A_LIPOIC_ACID: {
    name: "Alpha Liponsäure",
    info:
      "Alpha Liponsäure ist ein starkes Antioxidans und hilft dem Körper, freie Radikale, die durch den Stoffwechsel entstehen und Zellen schädigen können, zu neutralisieren.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Baut Kohlenhydrate ab",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Wirkt als Antioxidans",
      [GeneDataGroupType.DETOX]: "Unterstützt die Leberfunktion",
    },
  },
  Q10: {
    name: "Coenzym Q10",
    info:
      "Coenzym Q10 ist ein wichtiges Antioxidans, das unter anderem vom Körper selbst produziert werden kann und für seine Wirkung erst von einem Gen aktiviert werden muss. Da manche Menschen einen Gendefekt in diesem (NQO1) Gen tragen, sind sie nicht in der Lage, Coenzym Q10 zu aktivieren.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Produziert Energie in den Zellen",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Verbessert die antioxidative Enzymaktivität",
    },
  },
  CYSTEINE: {
    name: "Cystein",
    info:
      "Aminosäuren sind die Bausteine für Proteine und sind somit lebenswichtig. Cystein und Methionin sind solche Aminosäuren, die jedoch bei besonderen genetischen Typen in zu hoher Menge einen schlechten Einfluss auf die Knochen haben können.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Notwendig für den Schwefelstoffwechsel",
      [GeneDataGroupType.BONE]: "Hilft gesunde Knochen zu erhalten",
    },
  },
  HIGH_GLYC_INDEX: {
    name: "Hoher Glyx",
    info:
      "Nahrungsmittel mit einem hohen glykämischen Index enthalten Kohlenhydrate, die sehr schnell verdaut werden können und den Blutzuckerspiegel sehr rasch ansteigen lassen. In der Regel kann der Körper durch die richtige Ausschüttung von Insulin bei solchen Lebensmitteln entgegenwirken, doch bestimmte genetische Typen reagieren schlechter auf einen hohen Glyx Index und sollten diese Nahrungsmittel reduzieren.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Kann Herz-Kreislauf-Erkrankungen oder Diabetes verursachen",
      [GeneDataGroupType.EYE]: "Kann die Zellfunktion im Auge schädigen",
      [GeneDataGroupType.BRAIN]: "Kann zu Fettleibigkeit oder Fettsucht führen",
      [GeneDataGroupType.METABOLISM]: "Kann zu einem langsameren Stoffwechsel führen",
    },
  },
  COFFEE: {
    name: "Kaffee",
    info:
      "Kaffee enthält eine sehr hohe Menge an Antioxidantien und bietet deshalb für diverse Stoffwechselprobleme und Krankheiten, die mit der Entstehung von freien Radikalen zu tun haben, einen erheblichen Schutz. Das im Kaffee enthaltene Koffein kann sich jedoch negativ auf die Knochengesundheit auswirken und sollte bei bestimmten genetischen Typen gemieden werden.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Wirkt stimulierend",
      [GeneDataGroupType.HEART]: "Stimuliert die Herzkontraktionsintensität",
      [GeneDataGroupType.BONE]: "Verringert die Knochenmineraldichte",
      [GeneDataGroupType.EYE]: "Eine übermäßige Einnahme kann die Augengesundheit beeinträchtigen",
      [GeneDataGroupType.BRAIN]: "Erhöht den Energiestoffwechsel",
      [GeneDataGroupType.JOINT]: "Hat entzündungshemmende Eigenschaften",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Verringert Entzündungsmarker",
    },
  },
  METHIONINE: {
    name: "Methionin",
    info:
      "Aminosäuren sind die Bausteine für Proteine und sind somit lebenswichtig. Cystein und Methionin sind solche Aminosäuren, die jedoch bei besonderen genetischen Typen in zu hoher Menge einen schlechten Einfluss auf die Knochen haben können.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Unterstützt die Entgiftung der Leber und den Homocysteinzyklus",
      [GeneDataGroupType.BONE]: "Beeinflusst die Knochendichte",
    },
  },
  PURINE: {
    name: "Purine",
    info:
      "Purine werden vom Körper selbst produziert, können aber auch durch den Verzehr von tierischen Lebensmitteln (besonders Haut und Innereien) aufgenommen werden. Zu hohe Mengen an Purinen können sich bei bestimmten genetischen Typen schlecht auf die Gelenksgesundheit auswirken.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Liefert Energie und steuert die Zellfunktion",
      [GeneDataGroupType.JOINT]: "Liefert Energie und steuert die Zellfunktion",
    },
  },
  GLUTEN: {
    name: "Gluten",
    info:
      "Gluten ist ein Protein, das hauptsächlich in Weizen, Gerste und Roggen vorkommt. Wenn eine Person eine Glutenunverträglichkeit hat, kann dieses Protein Verdauungsprobleme verursachen.",
    functions: {
      [GeneDataGroupType.TOTAL]: "Kann eine Entzündungsreaktion hervorrufen",
      [GeneDataGroupType.GLUTEN]: "Kann eine Entzündungsreaktion hervorrufen",
    },
  },
};
const geneDataGroupTypeTranslations = {
  [GeneDataGroupType.HEART]: "Herz",
  [GeneDataGroupType.BRAIN]: "Gehirn",
  [GeneDataGroupType.BONE]: "Knochen",
  [GeneDataGroupType.JOINT]: "Gelenke",
  [GeneDataGroupType.EYE]: "Augen",
  [GeneDataGroupType.HOMOCYSTEINE]: "Homocystein",
  [GeneDataGroupType.HYPERTENSION]: "Blutdruck",
  [GeneDataGroupType.HOMOCYSTEINE_B2]: "Blutdruck",
  [GeneDataGroupType.GLUTEN]: "Gluten",
  [GeneDataGroupType.LACTOSE]: "Laktose",
  [GeneDataGroupType.OXIDATIVESTRESS]: "Oxidativer Stress",
  [GeneDataGroupType.DETOX]: "Entgiftung",
  [GeneDataGroupType.METABOLISM]: "Stoffwechsel",
  [GeneDataGroupType.MICRONUTRIENTS]: "Mikronährstoffe",
  [GeneDataGroupType.RDA]: "Tagesbedarf",
};

export const TRANSLATIONS_DE = {
  general: {
    digital_twin: "myDigitalTwin",
    demo: "Demo",
    personalize_your_own_digital_twin: "Personalize your own digital twin!",
    personalize_your_own_digital_twin_link: "https://www.mydigitaltwin.at/",
    create_your_own_personal_digital_twin: "Create your own personal digital twin",
    cancel: "Abbrechen",
    logout: "Ausloggen",
    privacy_policy: "Datenschutzerklärung",
    got_it: "Alles klar",
    status_progress: "Fortschritt",
    dont_show_this_again: "Diese Seite nicht mehr anzeigen.",
    of: "von",
  },
  date_format: {
    date: "dd.MM.yyyy",
    date_time: "dd.MM.yyyy HH:mm",
  },
  icons: {
    back: "Zurück",
    next: "Weiter",
    caffeine: "Koffein",
    fructose: "Fruktose",
    gluten: "Gluten",
    lactose: "Laktose",
    blood: "Blut",
    bookmark: "Favoriten",
  },
  portal: {
    inactivity_logout: "Sie wurden aufgrund von Inaktivität abgemeldet.",
  },
  app_bar: {
    personalize_your_own_digital_twin: "Personalize your own digital twin!",
    results: "Ergebnisse",
    search: "Suche",
    shop: "Shop",
    profile: "Profil",
    logout: "Ausloggen",
    favorites: "Favoriten",
    your_data: "Daten",
    your_reports: "Ergebnisse",
    logout_modal: {
      heading: "Willst du dich wirklich abmelden?",
    },
  },
  gender: {
    [Gender.MALE]: "Männlich",
    [Gender.FEMALE]: "Weiblich",
  },
  ethnicity: {
    [Ethnicity.AFRICAN]: "Afrikanisch",
    [Ethnicity.ARABIC]: "Arabisch",
    [Ethnicity.ASIAN]: "Asiatisch",
    [Ethnicity.EUROPEAN]: "Europäisch",
    [Ethnicity.HISPANIC]: "Hispanisch",
    [Ethnicity.MIXED]: "Mischung",
  },
  diet_preference: {
    [DietPreference.STANDARD]: "Standard",
    [DietPreference.PESCATARIAN]: "Pescetarisch",
    [DietPreference.VEGETERIAN]: "Vegetarisch",
    [DietPreference.VEGAN]: "Vegan",
  },
  login: {
    welcome: "Willkommen",
    sign_in: "Anmelden",
    sign_up: "Registrieren",
    log_in: "Einloggen",
    reset_password_action: "Zurücksetzen",
    forgot_password: "Passwort vergessen?",
    visit_our_shop: "Besuche unseren Shop!",
    register_email_info: "Verwende bitte die gleiche E-Mail-Adresse, wenn du unser DNA-Kit bereits gekauft hast.",
    code_info: "Wir haben dir einen Code per E-Mail geschickt. Bitte gib ihn hier ein.",
    reset_password_info: "Wir haben dir einen Link zum Zurücksetzen deines Passworts per E-Mail geschickt.",
    change_password: "Passwort Ändern",
    change_password_action: "Ändern",
    change_password_info: "Dein Passwort wurde erfolgreich geändert.",
    resend_code: "Code erneut senden",
    fields: {
      email: "E-Mail-Adresse",
      password: "Passwort",
      repeat_password: "Passwort wiederholen",
      firstname: "Vorname",
      lastname: "Nachname",
    },
    errors: {
      email_already_registered: "Diese E-Mail ist bereits registriert.",
      email_invalid: "Dies ist keine gültige E-Mail-Adresse",
      password_too_short: "Min. 8 Zeichen!",
      passwords_dont_match: "Die Passwörter stimmen nicht überein!",
    },
  },
  onboarding: {
    introduction: {
      header: "Hallo!\nIch werde dein digitales Ich.",
      description: "Bitte gib folgende Daten an, damit ich dir möglichst ähnlich bin.",
      button: "Los!",
    },
    firstname: {
      label: "Vorname",
      header: "Wie heißt du mit Vornamen?",
    },
    lastname: {
      label: "Nachname",
      header: "Wie heißt du mit Nachnamen?",
    },
    gender: {
      label: "Geschlecht",
      header: "Was ist dein Geschlecht?",
    },
    weight: {
      label: "Gewicht (kg)",
      header: "Welches Gewicht hast du derzeit (kg)?",
    },
    date_of_birth: {
      label: "Geburtstag",
      header: "An welchem Tag bist du geboren?",
    },
    height: {
      label: "Größe (cm)",
      header: "Wie groß bist du (cm)?",
    },
    ethnicity: {
      label: "Ethnische Zugehörigkeit",
      header: "Welcher Ethnie fühlst du dich zugehörig?",
    },
    diet_preference: {
      label: "Ernährungspräferenz",
      header: "What is your diet preference?", // TODO: Translate
    },
    next: {
      button: "Next",
    },
  },
  api_errors: {
    OTHER: "Unbekannter Fehler ist aufgetreten.",
    user: {
      INTERNAL_SERVER_ERROR: "Ein unbekannter Fehler ist aufgetreten!",
    },
    login: {
      [LoginError.CREDENTIALS_INVALID]: "E-Mail oder Passwort falsch!",
      [LoginError.UNKNOWN_USER]: "Diese E-Mail-Adresse ist nicht registriert",
      [RegisterError.INVALID_DATA]: "Überprüfe deine Eingaben!",
      [RegisterError.EMAIL_ALREADY_IN_USE]: "Diese E-Mail-Adresse ist bereits registriert",
      [VerifyCodeError.CODE_INVALID]: "Code ist ungültig oder abgelaufen!",
      [PasswordResetError.UNKNOWN_USER]: "Diese E-Mail-Adresse ist nicht registriert",
      ACCESS_DENIED: "Zugriff verweigert!",
      INTERNAL_SERVER_ERROR: "Ein unbekannter Fehler ist aufgetreten!",
    },
    update_password: {
      [UpdatePasswordError.INVALID_PASSWORD]: "Das alte Passwort darf nicht erneut verwendet werden.",
    },
  },
  menu_item_category: {
    [MenuItemCategory.RECOMMENDATIONS]: "Deine Empfehlungen",
    [MenuItemCategory.ORGANS]: "Organe und Körper",
    [MenuItemCategory.DIET]: "Ernährung",
    [MenuItemCategory.METABOLISM]: "Stoffwechsel",
    [MenuItemCategory.BLOOD]: "Blut",
    [MenuItemCategory.MENTAL_HEALTH]: "Psychische Gesundheit",
    [MenuItemCategory.MICRONUTRIENTS]: "Mikronährstoffe",
    [MenuItemSubcategory.GENEVARIATIONS]: "Genvariationen",
    [MenuItemCategory.USER]: "Profil",
  },
  gene_data_group_area: {
    [GeneDataGroupArea.ORGANS]: "Organe und Körper",
    [GeneDataGroupArea.DIET]: "Ernährung",
    [GeneDataGroupArea.METABOLISM]: "Stoffwechsel",
    [GeneDataGroupArea.BLOOD]: "Blut",
    [GeneDataGroupArea.MICRONUTRIENTS]: "Mikronährstoffe",
  },
  menu_item_subcategories: {
    ...geneDataGroupTypeTranslations,
    MICRONUTRIENTS_TO_INCREASE: "Mikronährstoffe",
    OVERVIEW: "Übersicht",
    FINDINGS: "Befunde",
    GENEVARIATIONS: "Genvariationen",
    FRUCTOSE: "Fruktose",
    USER_DATA: "Deine Daten",
    RESILIENCE: "Resilienz",
    navigate_to: {
      [GeneDataGroupType.HEART]: "Weiter zu Herz",
      [GeneDataGroupType.BRAIN]: "Weiter zu Gehirn",
      [GeneDataGroupType.BONE]: "Weiter zu Knochen",
      [GeneDataGroupType.JOINT]: "Weiter zu Gelenke",
      [GeneDataGroupType.EYE]: "Weiter zu Augen",
      [GeneDataGroupType.HOMOCYSTEINE]: "Weiter zu Blut",
      [GeneDataGroupType.HYPERTENSION]: "Weiter zu Blutdruck",
      [GeneDataGroupType.HOMOCYSTEINE_B2]: "Weiter zu Vitamin B2",
      [GeneDataGroupType.GLUTEN]: "Weiter zu Gluten",
      [GeneDataGroupType.LACTOSE]: "Weiter zu Laktose",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Weiter zu Oxidativer Stress",
      [GeneDataGroupType.DETOX]: "Weiter zu Entgiftung",
      [GeneDataGroupType.METABOLISM]: "Weiter zu Stoffwechsel",
      [GeneDataGroupType.MICRONUTRIENTS]: "Weiter zu Mikronährstoffe",
      [GeneDataGroupType.RDA]: "Weiter zu Tagesbedarf",
      MICRONUTRIENTS_TO_INCREASE: "Weiter zu Mikronährstoffe",
      GENEVARIATIONS: "Weiter zu Genvariationen",
      RESILIENCE: "Weiter zu Resilienz",
    },
  },
  gene_data_group_type: geneDataGroupTypeTranslations,
  gene: {
    orientation: "Orientierung",
    name: "Symbol",
    refsnp: "rsNCBI",
    genotype: "Geno Typ",
    genoTypeNoSpace: "Genotyp",
  },
  nutrients_to_increase: {
    heading: "Mikronährstoffe",
    subheading:
      "Basierend auf deinen Testergebnissen empfehlen wir dir, von folgenden Nährstoffen mehr zu dir zu nehmen.",
  },
  nutrients_to_decrease: {
    heading: "Mikronährstoffe",
    subheading:
      "Basierend auf deinen Testergebnissen empfehlen wir dir, von folgenden Nährstoffen weniger zu dir zu nehmen.",
  },
  micronutrient: {
    name: "Mikronährstoff",
    recommendation: "RDA",
    value: "Dein Tagesbedarf",
    unit: "Einheit",
    of: "von",
    not_specified: "Keine Angabe",
    rda_not_specified: "Kein Referenzwert bekannt",
    disposition: "Relevanz",
    explanation: {
      heading: "Informationen zu Nährstoffen, die du häufiger oder seltener aufnehmen solltest, sehen so aus",
      hero: {
        card: {
          header: "Vitamin B1",
          line_1: "Abbau von Fettsäuren",
          line_2: "Fleisch, Fisch, Eier, Milch",
          line_3: "6,1 µg (244% von Ø 2,5 µg)",
          bottom_line_1: "Relevanz",
          bottom_line_2: "Essenziell",
          bottom_line_3: "Aktueller Wert",
          bottom_line_4: "Leichter Mangel",
        },
        descriptions: {
          top_left: {
            line_1: "Die Hauptfunktion des ",
            line_2: "Nährstoffs (&nbsp;&nbsp;&nbsp;) und wo",
            line_3: "er in höheren Mengen (&nbsp;&nbsp;&nbsp;)",
            line_4: "vorkommt.",
            line_1_x: "55.2490234",
            line_2_x: "55.2832031",
            line_3_x: "31.1318359",
            line_4_x: "127.662109",
            nutrient_icon: {
              x: "127",
              y: "22",
            },
            chef_icon: {
              x: "176",
              y: "38",
            },
          },
          top_center: {
            line_1: "Um diesen Nährstoff geht’s.",
          },
          top_right: {
            line_1: "Hier kannst du.",
            line_1_x_mobile: "180",
            line_2: "mehr nachlesen.",
            line_2_x_mobile: "180",
          },
          center_left: {
            line_1: "Dein persönlicher Tages-",
            line_2: "bedarf (&nbsp;&nbsp;&nbsp;) im Vergleich zum",
            line_3: "durchschnittlich empfohlenen",
            line_4: "Tagesbedarf. (RDA)",
            line_1_x: "36.59375",
            line_2_x: "17.1660156",
            line_3_x: "7.0078125",
            line_4_x: "67.7314453",
            cutlery_icon: {
              x: "65",
              y: "123",
            },
          },
          center_right: {
            line_1: "Das Ergebnis deiner",
            line_2: "Blutanalysen zeigt dir, ob ",
            line_3: "ein Mangel (&nbsp;&nbsp;&nbsp;) oder eine ",
            line_4: "ausreichende Ver-",
            line_5: "sorgung besteht (&nbsp;&nbsp;&nbsp;) und ",
            line_6: "wie sich dieser Wert zur ",
            line_7: "letzten Messung (&nbsp;&nbsp;&nbsp;)  ",
            line_8: "verändert hat.",
            icon_1: {
              cx: "82.5730069",
              cy: "45",
            },
            icon_2: {
              cx: "114.573007",
              cy: "78.5",
            },
            icon_3: {
              cx: "116.073007",
              cy: "112",
            },
          },
          bottom_left: {
            line_1: "Deine Genanalyse zeigt dir,",
            line_2: "wie wichtig es ist,",
            line_3: "diesen Nährstoff zu reduzieren,",
            line_4: "oder zu erhöhen.*",
            line_1_x: "23",
            line_2_x: "86",
            line_3_x: "0",
            line_4_x: "79",
          },
          bottom_center: {
            line_1: "* Diese Empfehlung bezieht sich auf den",
            line_2: "gerade ausgewählten Bereich deines Körpers.",
          },
          bottom_right: {
            line_1: "Trend fallend",
            line_2: "Trend steigend",
          },
        },

        mobile_descriptions: {
          top_left: {
            line_1: "Deine Genanalyse",
            line_2: "zeigt dir, wie",
            line_3: "wichtig es ist, diesen",
            line_4: "Nährstoff zu ",
            line_5: "reduzieren, oder",
            line_6: "zu erhöhen.*",
          },
          top_right: {
            line_1: "Das Ergebnis deiner",
            line_2: "Blutanalysen zeigt dir,",
            line_3: "ob ein Mangel (&nbsp;&nbsp;&nbsp;) oder",
            line_4: "eine ausreichende Ver-",
            line_5: "sorgung besteht (&nbsp;&nbsp;&nbsp;)",
            line_6: "und wie sich dieser",
            line_7: "Wert zur letzten",
            line_8: "Messung (&nbsp;&nbsp;&nbsp;) verändert",
            line_9: "hat.",
            icon_1: {
              cx: "71.5",
              cy: "145.5",
            },
            icon_2: {
              cx: "104.5",
              cy: "50",
            },
            icon_3: {
              cx: "122",
              cy: "88.5",
            },
          },
          middle_left: {
            line_1: "* Diese Empfehlung",
            line_2: "bezieht sich auf den",
            line_3: "gerade ausgewählten",
            line_4: "Bereich deines Körpers.",
          },
          bottom_1: {
            line_1: "Hier erfährst du von der Hauptfunktion des",
            line_2: "Nährstoffs (&nbsp;&nbsp;&nbsp;) und wo er in höheren",
            line_3: "Mengen (&nbsp;&nbsp;&nbsp;) vorkommt.",
            wrench_x: "78",
            wrench_y: "147",
            chef_hat_x: "62",
            chef_hat_y: "167.5",
          },
          bottom_2: {
            line_1: "Dein persönlicher Tages-bedarf (&nbsp;&nbsp;&nbsp;) im",
            line_2: "Vergleich zum durchschnittlich empfoh-",
            line_3: "lenen Tagesbedarf. (RDA)",
            cutlery_x: "214",
            cutlery_y: "210",
          },
        },
      },
    },
  },
  overview: {
    heading: "Willkommen zu deinen Testergebnissen!", //"Willkommen {{firstname}} {{lastname}}!",
    subheading_1:
      "Auf den folgenden Seiten erzähle ich dir die wichtigsten Erkenntnisse aus deiner Genetik. Du kannst natürlich über das Menü links noch einen tieferen Einblick erhalten.",
    subheading_2: "",
    subheading_3:
      "Lass uns mit Informationen starten, die dein größtes, inneres Organ (den Darm) und somit einen großen Teil deiner Gesundheit beeinflussen: Deine <0>Ernährung</0> und mögliche Unverträglichkeiten.",
    subheading_demo_1:
      "Hier findest du die Beispielergebnisse für Mica, unserem digitalen Demo-Zwilling. Mica wird über ihr eigenes, individuelles genetisches Profil erstellt. Für deine eigenen Ergebnisse, bestelle heute noch dein persönliches Testkit.",
    subheading_demo_2:
      "Deine Gene zeigen dir wertvolle Einblicke in deinen Körper, die dir dabei helfen, lange gesund und fit zu bleiben. Der digitale Avatar führt dich interaktiv durch deinen eigenen Körper und stellt alle Ergebnisse leicht verständlich dar. Du kannst deinen digitalen Avatar auch drehen und rotieren, um ihn von verschiedenen Seiten zu betrachten.",
    subheading_demo_3:
      "Für eine kleine Einführung möchten wir Micas genetisches Risiko für eine Gluten- oder Laktoseintoleranz betrachten. Außerdem spannend für alle Kaffeetrinker – wie gut kann Mica eigentlich Koffein verwerten?",

    percent_of_population: "{{percent}}% der Bevölkerung",
    caffeine: {
      name: "Koffein-\nverarbeitung",
      breakdown: {
        [CaffeineBreakdown.NORMAL]: "Normale Effizienz",
        [CaffeineBreakdown.SLOW]: "Geringe Effizienz",
      },
    },
    lactose: {
      name: "Laktose-\nintoleranz",
      intolerance: {
        [Intolerance.HIGH_RISK]: "Sehr hohes Risiko",
        [Intolerance.NO_RISK]: "Kein erhöhtes Risiko",
      },
    },
    gluten: {
      name: "Gluten-\nunverträglichkeit",
      intolerance: {
        [Intolerance.HIGH_RISK]: "Sehr hohes Risiko",
        [Intolerance.MODERATE_RISK]: "Mäßiges Risiko",
        [Intolerance.NO_RISK]: "Kein erhöhtes Risiko",
      },
    },
    arrow_text: "Mehr dazu findest du im Kapitel Ernährung",
  },
  gene_variations: {
    heading: "Deine Genvariationen",
    subheading:
      "Unten siehst du, welche Gene analysiert wurden, deinen Genotyp und wie sie mit deinem Körper zusammenhängen:",
    types: {
      [GeneDataGroupType.HEART]: "Herzgesundheit",
      [GeneDataGroupType.BRAIN]: "Gehirn",
      [GeneDataGroupType.BONE]: "Knochen",
      [GeneDataGroupType.JOINT]: "Gelenke",
      [GeneDataGroupType.EYE]: "Augen",
      [GeneDataGroupType.HOMOCYSTEINE]: "Homocystein",
      [GeneDataGroupType.HYPERTENSION]: "Blutdruck",
      [GeneDataGroupType.HOMOCYSTEINE_B2]: "Homocystein B2",
      [GeneDataGroupType.GLUTEN]: "Gluten",
      [GeneDataGroupType.LACTOSE]: "Laktose",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Oxidativer Stress",
      [GeneDataGroupType.DETOX]: "Entgiftung",
      [GeneDataGroupType.METABOLISM]: "Stoffwechsel",
      [GeneDataGroupType.MICRONUTRIENTS]: "Mikronährstoffe",
      [GeneDataGroupType.RDA]: "Tagesbedarf",
    },
    page_1: {
      header: "Deine Genvariationen",
      explanation_1:
        "Alle menschlichen Zellen bestehen aus Chromosomen, die unsere Erbinformtion in Form von spiralförmigen DNS-Stränge beinhalten. Bestimmte Sequenzen der DNS wurden als Gene definiert. Je nach Aufbau des Gens, kann es höhere Risiken für Krankheiten geben. Dieses Risiko kann mithilfe von Genanalysen bestimmt werden.",
      explanation_2:
        "Während dein gesamtes Genom, also die Summe aller DNS-Sequenzen, einzigartig ist, können einzelne Gene mit verschiedenen Personen ident sein.",
      nucleus:
        "Im Zellkern unserer 50 Billionen Zellen befinden sich je 23 geerbte Chromosomenpaare. Jedes Chromosom enthält einen spiralförmigen DNA-Strang, die Doppelhelix.",
      double_helix:
        "Die Doppelhelix besteht aus vier Nukleinbasen. Dabei können sich nur A -T und C-G Verbindungen bilden.",
      dna:
        "Innerhalb der DNS bilden gewisse Sequenzen Gene. Sie weisen die Zelle an, welche Proteine sie bildet und damit, welche Funktionen sie ausübt. ",
      adenin: "Adenin",
      cytocin: "Cytocin",
      thymin: "Thymin",
      guanin: "Guanin",
    },
    page_2: {
      explanation:
        "Viele Gene sind an unterschiedlichen Prozessen beteiligt, weshalb noch nicht alle Auswirkungen von diversen Kombinationen klar sind. 98% unserer DNS ist überhaupt noch unerforscht.",
      body_1:
        "Auf der folgenden Seite findest du die Zuordnung zu Körperbereichen, wo definitiv klar ist, dass die gelisteten Gene eine Rolle spielen.",
      body_2: "Informationen zu deinen Genen, sehen so aus:",
      hero: {
        mobile: {
          bottom_1: {
            line_1: "Der Genotyp beschreibt die genetische ",
            line_2: "Ausstattung, also den Aufbau des Gens.",
          },
          bottom_2: {
            line_1: "Die Symbole zeigen dir, in welcher ",
            line_2: "Orientierung das Gen sequenziert wurde.",
          },
          bottom_right: {
            line_1: "Dieser Wert klassifiziert",
            line_2: "den Strang, in dem",
            line_3: "dieses Gen zu finden ist.",
            line_4: "",
          },
        },
        right_top: {
          line_1: "Hier kannst du",
          line_2: "mehr nachlesen.",
        },
        right_middle: {
          line_1: "Dieser Anteil der",
          line_2: "Weltbevölkerung",
          line_3: "hat dasselbe,",
          line_4: "idente Gen wie du.",
        },
        left_top: {
          line_1: "Die Symbole zeigen dir, in",
          line_2: "welcher Orientierung das",
          line_3: "Gen sequenziert wurde.",
        },
        left_middle: {
          line_1: "Vorwärtsstrang",
          line_2: "Umgekehrter Strang",
        },
        bottom_left: {
          line_1: "Der Genotyp beschreibt die",
          line_2: "genetische Ausstattung,",
          line_3: "also den Aufbau des Gens.",
        },
        bottom_right: {
          line_1: "Dieser Wert klassifiziert",
          line_2: "den Strang, in dem",
          line_3: "dieses Gen zu finden ist.",
        },
        top_middle: "Um dieses Gen geht’s.",
        genotype: "Genotyp",
      },
    },
  },
  nutrient: {
    increase: "Erhöhen",
    neutral: "Neutral",
    decrease: "Verringern",
    sources: "Sources", // TODO: Translate
    source_values: nutrientSourcesDE,
    values: {
      ...nutrientTranslations,
      Folsäure: nutrientTranslations.FOLIC_ACID,
      VitaminA: nutrientTranslations.VIT_A,
      VitaminB2: nutrientTranslations.VIT_B2,
      VitaminB6: {
        ...nutrientTranslations.VIT_B6,
        name: "Vitamin B6",
        info: "",
      },
      VitaminB12: {
        ...nutrientTranslations.VIT_B6,
        name: "Vitamin B12",
        info: "",
        functions: {
          [GeneDataGroupType.TOTAL]: "Unterstützt die Erythrozyten- und DNA-Produktion",
          [GeneDataGroupType.BRAIN]: "Verhindert Neuronenverlust und Hirnatrophie",
        },
      },
      VitaminC: nutrientTranslations.VIT_C,
      VitaminD3: nutrientTranslations.VIT_D3,
      VitaminE: nutrientTranslations.VIT_E,
      Eisen: nutrientTranslations.IRON,
      Kalzium: nutrientTranslations.CALCIUM,
      Kupfer: nutrientTranslations.COPPER,
      Magnesium: nutrientTranslations.MAGNESIUM,
      Mangan: nutrientTranslations.MANGANESE,
      Selen: nutrientTranslations.SELENE,
      Zink: nutrientTranslations.ZINC,
      Lutein: nutrientTranslations.LUTEINE,
      MSM: nutrientTranslations.MSM,
      Phytosterol: nutrientTranslations.PHYTOSTEROL,
      Alpha_Liponsäure: nutrientTranslations.A_LIPOIC_ACID,
      Alpha_Linolensäure: nutrientTranslations.A_LINOLENIC_ACID,
      Omega3: nutrientTranslations.OMEGA3,
      Coenzym_Q10: nutrientTranslations.Q10,
    },
    disposition: {
      recommendation: "Relevanz",
      importance_level: {
        neutral: "Neutral",
        slightly: "Kaum relevant",
        moderately: "Eher wichtig",
        important: "Wichtig",
        very_important: "Sehr wichtig",
        essential: "Essentiell",
      },
    },
    functions: {
      [GeneDataGroupType.BONE]: "Bones",
      [GeneDataGroupType.BRAIN]: "Brain",
      [GeneDataGroupType.DETOX]: "Detox",
      [GeneDataGroupType.EYE]: "Eyes",
      [GeneDataGroupType.GLUTEN]: "Gluten",
      [GeneDataGroupType.HEART]: "Heart",
      [GeneDataGroupType.HOMOCYSTEINE]: "Homocycteine",
      [GeneDataGroupType.HOMOCYSTEINE_B2]: "Homocycteine B2",
      [GeneDataGroupType.HYPERTENSION]: "Hypertension",
      [GeneDataGroupType.JOINT]: "Joint",
      [GeneDataGroupType.LACTOSE]: "Lactose",
      [GeneDataGroupType.METABOLISM]: "Metabolism",
      [GeneDataGroupType.OXIDATIVESTRESS]: "Oxidative Stress",
    },
  },
  twin_communication: {
    link_to_genes: "Überprüfung des analysierten Gens.",
    result_heading: "Dein Ergebnis",
    bad_image_description: "Dein Ergebnis",
    good_image_active_description: "Dein Ergebnis",
    good_image_inactive_description: "Optimal",
    answer: {
      [FeedbackType.STORYDATA]: {
        heading: "Daten",
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        heading: "Empfehlung",
      },
      [FeedbackType.GENES]: {
        heading: "meine Gene",
      },
      [FeedbackType.NUTRIENTS]: {
        heading: "Empfehlung",
        description:
          "Basierend auf deinen Genen und den damit verbundenen Stärken und Schwächen solltest du bestimmte Lebensmittel und Nährstoffe erhöhen oder verringern, wobei allgemeine RDA-Werte als Grundlage dienen. Diese Empfehlungen werden auf der Grundlage deines genetischen Profils berechnet.",
      },
      [FeedbackType.NUTRIENTS_TO_INCREASE]: {
        heading: "Mikronährstoffe",
        description:
          "Basierend auf deinen Genen und den damit verbundenen Stärken und Schwächen solltest du bestimmte Lebensmittel und Nährstoffe erhöhen, wobei allgemeine RDA-Werte als Grundlage dienen. Diese Empfehlungen werden auf der Grundlage deines genetischen Profils berechnet.",
        empty: "Es gibt keine Mikronährstoffe, die erhöht werden müssen.",
      },
      [FeedbackType.NUTRIENTS_TO_DECREASE]: {
        heading: "Mikronährstoffe",
        description:
          "Basierend auf deinen Genen und den damit verbundenen Stärken und Schwächen solltest du bestimmte Lebensmittel und Nährstoffe verringern, wobei allgemeine RDA-Werte als Grundlage dienen. Diese Empfehlungen werden auf der Grundlage deines genetischen Profils berechnet.",
        empty: "Es gibt keine Mikronährstoffe, die verringert werden müssen.",
      },
      [FeedbackType.INTRODUCTION]: {
        heading: "Einführung",
      },
      [FeedbackType.RESILIENCE]: {
        heading: "Resilienz",
        description_1:
          "Unsere psychische Gesundheit hat einen großen Einfluss auf unsere Lebensqualität. Besitzen wir nicht die Fähigkeit uns an Veränderungen nicht anpassen (Resilienz) und sind langfristig gestresst, kann das die körperliche Gesundheit beeinflussen.",
        description_2:
          "Unsere Resilienz wird nicht nur durch Erfahrungen beeinflusst, sondern auch durch biologische Faktoren wie unsere Gene.",
      },
      [FeedbackType.MICRONUTRIENTS]: {
        heading: "Dein empfohlener Tagesbedarf",
        description:
          "Basierend auf deinen Genen und den damit verbundenen Stärken und Schwächen solltest du bestimmte Lebensmittel und Nährstoffe erhöhen oder verringern, wobei allgemeine RDA-Werte als Grundlage dienen. Diese Empfehlungen werden auf der Grundlage deines genetischen Profils berechnet.",
      },
    },
    additional_texts: {
      [FeedbackType.STORYDATA]: {
        heading: "Wusstest du?",
        positive: "Ja, erzähl mir mehr!",
        negative: "Nein danke!",
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        heading: "Empfehlung",
        positive: "Ja bitte!",
        negative: "Nein danke!",
      },
      [FeedbackType.GENES]: {
        heading: "Wusstest du?",
        positive: "Ja, erzähl mir mehr!",
        negative: "Nein danke!",
      },
      [FeedbackType.NUTRIENTS]: {
        heading: "Empfehlung",
        positive: "Ja bitte!",
        negative: "Nein danke!",
      },
      [FeedbackType.INTRODUCTION]: {
        heading: "Einführung",
        positive: "Ja bitte!",
        negative: "Nein danke!",
      },
      [FeedbackType.MICRONUTRIENTS]: {
        heading: "Empfehlung",
        positive: "Ja bitte!",
        negative: "Nein danke!",
      },
    },

    TRIG_INFORMATION: {
      header: "Triglyceride",
      [FeedbackType.STORYDATA]: {
        question:
          "Triglyceride als Blutfett  sind ein unverzichtbarer Energiespeicher für den Körper, aber zu viel kann sich negativ auf die Herzgesundheit auswirken. Möchtest du wissen, ob du Triglyceride normal regulieren kannst?",
        description:
          "Triglyceride zählen (wie Cholesterin) zu den Blutfetten und sind als Energiespeicher für den Körper unverzichtbar. Werden die Bausteine für Triglyceride aus der Nahrung vermehrt aufgenommen, produziert der Körper übermäßig viele Triglyceride. Zu hoher Konsum von Nahrungsfetten kann deshalb zu erhöhten Triglyceridwerten führen, was sich negativ auf die Herzgesundheit auswirken kann. Bei manchen Menschen können jedoch auch genetische Variationen zu erhöhten Triglyceridwerten führen.",
        GOOD:
          "Gute Nachrichten! Dein Körper kann Triglyceride normal regulieren, daher hast du keine Veranlagung für einen erhöhten Triglyceridspiegel.",
        GOOD_list: [
          "Triglyceride können über die Nahrung aufgenommen werden.",
          "Triglyceride werden auch vom Körper produziert.",
          "Deine Fähigkeit Triglyceride zu regulieren ist normal.",
          "Triglyceride werden reduziert.",
          "Das Ergebnis ist, dass dein Triglyceridspiegel normal ist.",
        ],
        BAD: "Aufgrund deiner genetischen Variation neigst du zu einem erhöhten Triglyceridspiegel.",
        BAD_list: [
          "Triglyceride können über die Nahrung aufgenommen werden.",
          "Triglyceride werden auch vom Körper produziert.",
          "Deine Fähigkeit Triglyceride zu regulieren ist beeinträchtigt",
          "Triglyceride werden nicht ausreichend reduziert.",
          "Dies kann dazu führen, dass dein Triglyceridspiegel zu hoch ist.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question:
          "Möchtest du eine Empfehlung basierend auf deiner Fähigkeit Blutfette wie Triglyceride zu regulieren?",
        GOOD:
          "Glückwunsch! Dein Körper kann deine Triglyceride normal regulieren und du hast keine Veranlagung zu erhöhten Werten durch Nahrungsfette aus der Ernährung. Schau bitte aber trotzdem darauf hochwertige Fette zu dir zu nehmen.",
        BAD:
          "Durch deine genetische Variation hast du leider eine Veranlagung zu erhöhten Tricyceridwerten. Du kannst dem entgegenwirken indem du dich z.B. zuckerarm ernährst und auf Alkohol verzichtest. Wenn du gerne Fisch magst, schau darauf z.B. Lachs, Hering oder Sardinen zu essen, die gute Fettsäuren enthalten. Sei außerdem aktiv und treibe viel Sport.",
      },
    },
    CHOL_INFORMATION: {
      header: "Cholesterin",
      [FeedbackType.STORYDATA]: {
        question:
          "Cholesterin ist ein lebenswichtiges Blutfett und seine Regulation im Körper kann durch deine Gene beeinflusst werden. Möchtest du wissen, ob du Cholesterin optimal regulieren kannst?",
        description:
          'Cholesterin ist ein lebensnotwendiges Blutfett und wichtiger Grundbaustein für zahlreiche Stoffwechselvorgänge. Dabei wird zwischen dem "guten" (HDL) und "schlechten" (LDL) Cholesterin unterschieden. Cholesterin wird sowohl vom Körper selbst produziert, als auch über die Nahrung aufgenommen. Dabei ist die Menge entscheidend, da schlechte Cholesterinwerte mit einer Vielzahl gesundheitlicher Probleme in Verbindung stehen. An der Regulierung der Cholesterinwerte sind mehrere Gene beteiligt, die durch genetische Variationen in ihrer Funktion eingeschränkt sein können.',
        GOOD:
          "Gute Neuigkeiten! Dein Körper kann deinen Cholesterinspiegel normal regulieren, daher hast du keine Veranlagung für einen hohen Cholesterinspiegel.",
        GOOD_list: [
          "Cholesterin kann in der Nahrung aufgenommen werden.",
          "Cholesterin wird auch vom Körper produziert.",
          "Deine Fähigkeit den Cholesterinspiegel zu regulieren ist normal.",
          "Dies bedeutet, dass dein Cholesterin reduziert wird.",
          "Das Ergebnis ist, dass dein Cholesterinspiegel normal ist.",
        ],
        BAD: "Aufgrund deiner genetischen Variation neigst du zu einem hohen Cholesterinspiegel.",
        BAD_list: [
          "Cholesterin kann in der Nahrung aufgenommen werden.",
          "Cholesterin wird auch vom Körper produziert.",
          "Deine Fähigkeit den Cholesterinspiegel zu regulieren ist beeinträchtigt.",
          "Dies bedeutet, dass dein Cholesterinspiegel nicht ausreichend gesenkt wird.",
          "Dies kann dazu führen, dass dein Cholesterinspiegel zu hoch ist.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question: "Möchtest du eine Empfehlung basierend auf deiner Fähigkeit Blutfette wie Cholesterin zu regulieren?",
        GOOD:
          "Glückwunsch! Tu dir trotzdem etwas Gutes, indem du regelmäßig Bewegung machst und auf eine gesunde Ernährung achtest.",
        BAD:
          "Mach regelmäßig Sport und Bewegung und reduziere deinen Alkohol-, Nikotin- und Fleischkonsum. Achte bitte zusätzlich auf eine fett- und zuckerarme Ernährung.",
      },
    },
    OMEGA3_INFORMATION: {
      header: "OMEGA-3-Fettsäuren",
      [FeedbackType.STORYDATA]: {
        question:
          'OMEGA-3-Fettsäuren können helfen, das "schlechte" LDL Cholesterin zu senken und das "gute" HDL Cholesterin zu erhöhen. Dies hängt jedoch von deinen Genen ab. Möchtest du wissen, ob du mit OMEGA-3 deine Cholesterinwerte verbessern kannst?',
        description:
          'Omega-3-Fettsäuren spielen im Stoffwechsel unseres Körpers eine zentrale Rolle. Neben vielen anderen positiven Eigenschaften können sie auch die Herzgesundheit verbessern. Studien haben gezeigt, dass Omega-3-Fettsäuren einen positiven Effekt auf die Cholesterinwerte haben können, indem sie die "schlechten" LDL-Cholesterinwerte senken und das "gute" HDL-Cholesterin erhöhen. Dieser Effekt kann jedoch von einer genetischen Variation im APOA1-Gen verändert werden.',
        GOOD:
          "Sieht großartig aus! Dein APOA1-Gen funktioniert normal, sodass dein HDL-Cholesterinspiegel durch Omega-3-Fettsäuren verbessert werden kann.",
        GOOD_list: [
          "Omega-3-Fettsäuren (DHA / EPA) können über die Nahrung aufgenommen werden.",
          "HDL(„gutes“ Cholesterin) wird vom Körper produziert.",
          "Dein APOA1 - Gen funktioniert normal.",
          "Dies bedeutet, dass Omega - 3 - Fettsäuren einen positiven Einfluss auf deinen HDL - Spiegel haben.",
          "Das Ergebnis ist, dass dein HDL - Cholesterinspiegel dadurch erhöht wird.",
        ],
        BAD:
          "Aufgrund deiner genetischen Veranlagung ist die Funktion deines APOA1-Gens beeinträchtigt. Dadurch wird dein HDL-Cholesterinspiegel durch Omega-3-Fettsäuren nicht verbessert.",
        BAD_list: [
          "Omega-3-Fettsäuren (DHA / EPA) können über die Nahrung aufgenommen werden.",
          "HDL(„gutes“ Cholesterin) wird vom Körper produziert.",
          "Die Funktion deines APOA1 - Gens ist beeinträchtigt.",
          "Dies bedeutet, dass Omega - 3 - Fettsäuren keinen positiven Einfluss auf deinen HDL - Spiegel haben.",
          "Das Ergebnis ist, dass dein HDL - Cholesterinspiegel mit der Zeit sinkt.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question: "Möchtest du eine Empfehlung für die tägliche Verwendung von Omega-3 Fettsäuren?",
        GOOD:
          "Ergänze deine Nahrung mit hochwertigen Omega3-Ölen, wie z.B. Leinöl, Leindotteröl und Hanföl. Falls du einen Omega3-Mangel bzw. einen niedrigen HDL-Spiegel hast, nimm bitte zusätzlich ein hochwertiges Omega3-Präparat ein.",
        BAD:
          "Verwende bitte in der Küche hochwertige Omega3-Öle, aber verzichte auf die zusätzliche Zufuhr von Omega3-Präparaten, da diese nicht in der Lage sind deine HDL-Werte zu verbessern.",
      },
    },
    CALCIUM_INFORMATION: {
      header: "Kalzium",
      [FeedbackType.STORYDATA]: {
        question:
          "Die Aufnahme von Kalzium aus deiner Ernährung kann durch deine Gene beeinflusst werden. Kalzium ist wichtig für die Knochengesundheit. Möchtest du erfahren, ob du ausreichend Kalzium aus deiner Ernährung aufnehmen kannst?",
        description:
          "Kalzium ist ein wichtiger Mineralstoff, der über die Nahrung aufgenommen wird. Eine ausreichende Menge an Kalzium fördert nicht nur die Stabilität und Festigkeit der Knochen, sondern ist auch an anderen wichtigen Körperfunktionen beteiligt. Eine häufig vorkommende genetische Variation im LCT-Gen kann zu einer geringeren Aufnahme von Kalzium aus der Nahrung führen. Dadurch greift der Körper auf seine Reserven im Skelett zurück und schadet damit der Knochengesundheit. Eine erhöhte Zufuhr von kalziumreichen Lebensmittel kann diesem Effekt entgegenwirken.",
        GOOD:
          "Super! Du kannst Kalzium effektiv aus deiner Nahrung aufnehmen, was für die Knochengesundheit wichtig ist.",
        GOOD_list: [
          "Kalzium kann mit der Nahrung aufgenommen werden.",
          "Kalzium wird effektiv absorbiert.",
          "Dies bedeutet, dass eine ausreichende Calciumaufnahme möglich ist.",
        ],
        BAD: "Die Variation deines LCT-Gens kann zu einer verminderten Aufnahme von Kalzium aus der Nahrung führen.",
        BAD_list: [
          "Zusätzliches Kalzium kann in deiner Ernährung ergänzt werden.",
          "Dies kann die verringerte Absorption von Kalzium ausgleichen.",
          "Das Ergebnis ist eine ausreichende Kalziumaufnahme deines Körpers.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question:
          "Möchtest du eine Empfehlung für dich auf der Basis deiner Fähigkeit der Aufnahme von Kalzium aus der Nahrung?",
        GOOD:
          "Dein Körper ist in der Lage Kalzium effektiv aufzunehmen. Eine erhöhte Zufuhr ist deswegen nicht notwendig.",
        BAD:
          "Dein Körper verträgt eine extra Portion Kalzium. Um eine gute Knochengesundheit zu gewährleisten solltest du Kalzium als Nährstoffpräparat zuführen.",
      },
    },
    VDR_INFORMATION: {
      header: "Vitamin D",
      [FeedbackType.STORYDATA]: {
        question:
          "Vitamin D wird im Körper mit Hilfe der Sonne produziert. In seiner aktiven Form trägt es zu einer normalen Funktion deines Immunsystems bei und stärkt deine Knochen. Möchtest du wissen, ob du Vitamin D normal aufnehmen kannst?F",
        description:
          "Vitamin D kann sowohl vom Körper mit Hilfe von Sonnenstrahlung produziert, als auch über die Nahrung aufgenommen werden. Neben zahlreichen wichtigen Aufgaben steuert Vitamin D die Aufnahme von Kalzium und führt zu gesunden Knochen. Damit Vitamin D seine Wirkung entfalten kann, muss es zunächst in seine aktive Form umgewandelt werden und an den Vitamin D-Rezeptor (VDR) binden. Dieser Rezeptor befindet sich in fast jeder Körperzelle und kann nur in gebundener Form seine gesundheitsfördernden Aufgaben erfüllen. Genetische Variationen können die Struktur des Rezeptors verändern.",
        GOOD:
          "Lass die Sonne scheinen! Dein Vitamin D-Rezeptor (VDR) funktioniert und kann normal an Vitamin D binden.",
        GOOD_list: [
          "Vitamin D in einer normalen Dosis.",
          "Deine Vitamin D - Rezeptoren funktionieren normal.",
          "Du kannst vom positiven Vitamin - D - Effekt profitieren ohne zusätzliches Vitamin D zu supplementieren.",
        ],
        BAD:
          "Aufgrund deiner genetischen Veranlagung weist dein Vitamin D-Rezeptor (VDR) eine veränderte Struktur auf, die zu einer schlechten Fähigkeit führt, sich an Vitamin D zu binden.",
        BAD_list: [
          "Vitamin D gelangt in einer hohen Dosis in den Körper.",
          "Dein Vitamin D - Rezeptor ist jedoch defekt und kann daher Vitamin D(Calcidiol) nicht ausreichend in seine aktive Form umwandeln(Calcitriol).",
          "Dies bedeutet, dass eine erhöhte Zufuhr von Vitamin D erforderlich ist, damit Vitamin D seinen positiven Effekt entfalten kann.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question: "Möchtest du eine Empfehlung basierend auf der Effektivität deines Vitamin-D-Rezeptors?",
        GOOD:
          "Dein Körper hat keinen erhöhten Bedarf an Vitamin D3. Lass bitte trotzdem deinen Vitamin D3-Spiegel überprüfen, um einen möglichen Mangel auszuschließen.",
        BAD:
          "Dein Körper benötigt eine erhöhte Menge an Vitamin D3. Sonnenbaden alleine ist hier definitiv zu wenig. Lass bitte deinen Vitamin D3-Spiegel bestimmen und ergänze die dementsprechende Menge auch aufgrund deiner genetischen Variation.",
      },
    },
    OXI_INFORMATION: {
      header: "Freie Radikale",
      [FeedbackType.STORYDATA]: {
        question:
          "Schädliche freie Radikale zerstören Zellen und können den Alterungsprozess beschleunigen. Das ist als oxidativer Stress bekannt. Möchtest du erfahren, ob du freie Radikale ausreichend neutralisieren kannst?",
        description:
          "Beim Umwandeln von Energie entstehen schädliche Nebenprodukte, sogenannte freie Radikale. Diese zerstören Zellen und fördern den Alterungsprozess. Der Körper hat jedoch ein System, diese freien Radikale zu neutralisieren und unschädlich zu machen. Dieser Schutz wird von mehreren Genen kontrolliert. Häufig vorkommende Genvariationen können diese Funktion jedoch stören und den oxidativen Stress erhöhen.",
        GOOD:
          "Kein Stress! Freie Radikale werden ausreichend neutralisiert, sodass dein Körper keinem erhöhten oxidativen Stress ausgesetzt ist. Die normale Dosis von Antioxidantien ist daher ausreichend.",
        GOOD_list: [
          "Deine Gene bieten ausreichenden Schutz gegen freie Radikale.",
          "Dies bedeutet, dass die meisten freien Radikale neutralisiert werden.",
          "Es können aber auch bei Bedarf Antioxidantien eingenommen werden.",
        ],
        BAD:
          "Aufgrund deiner genetischen Veranlagung können freie Radikale nicht ausreichend neutralisiert werden. Dein Körper ist dadurch mit erhöhtem oxidativem Stress belastet. Eine erhöhte Menge an Antioxidantien stellt den Schutz für deinen Körper wieder her.",
        BAD_list: [
          "Deine Gene bieten zu wenig Schutz gegen freie Radikale.",
          "Antioxidantien können zum Schutz vor freien Radikalen eingenommen werden.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question: "Möchtest du eine Empfehlung basierend auf deiner Fähigkeit, freie Radikale zu neutralisieren?",
        GOOD:
          "Gratuliere! Dein Körper ist in der Lage freie Radikale ausreichend zu neutralisieren. Sei dennoch achtsam im Umgang mit Nikotin, Alkohol und anderen schädlichen Substanzen. Dein Körper wird es dir danken! Und gönn dir regelmäßig eine extra Portion Obst und Gemüse.",
        BAD:
          "Achte darauf, dass du wichtige Antioxidantien, wie Vitamin C, Vitamin E, Coenzym Q10 und Selen als Nährstoffpräparat ergänzt und erhöhe den Konsum von frischem Obst und Gemüse. Verzichte auf Nikotin und reduziere deinen Alkoholkonsum. Wo immer du Stress reduzieren kannst, tu es!",
      },
    },
    HOMO_INFORMATION: {
      header: "Homocystein",
      [FeedbackType.STORYDATA]: {
        question:
          "Wenn Proteine im Körper abgebaut werden, entsteht Homocystein, ein starkes Zytotoxin, das deine Gesundheit schädigen kann. Möchtest du wissen, ob du Homocystein ausreichend abbauen kannst?",
        description:
          "Homocystein ist ein Stoff, der im Körper beim Abbau von Eiweiß entsteht. Da Homocystein ein starkes Zellgift ist, muss es rasch abgebaut werden, wozu die Vitamine B6 und B12 sowie Folsäure wichtig sind. An einem Teil dieses Mechanismus ist das MTHFR-Gen beteiligt. Häufig vorkommende genetische Variationen können jedoch dazu führen, dass dieses Gen nicht ausreichend funktioniert und damit der Abbau von Homocystein eingeschränkt ist. Dies führt in vielen Fällen zu einem erhöhten Homocysteinwert im Blut.",
        GOOD:
          "Es sieht gut aus! Die Aktivität deines MTHFR-Gens ist uneingeschränkt, sodass du Homocystein ausreichend reduzieren kannst.",
        GOOD_list: [
          "Deine Gene können das MTHFR-Enzym produzieren, das Schutz gegen Homocystein bietet.",
          "Dein Ergebnis ist, dass Homocystein problemlos abgebaut werden kann.",
        ],
        BAD:
          "Aufgrund deiner genetischen Veranlagung ist die Aktivität deines MTHFR-Gens eingeschränkt. Das bedeutet, dass Homocystein in deinem Körper nicht ausreichend reduziert werden kann.",
        BAD_list: [
          "Deine Gene bieten keinen Schutz gegen Homocystein.",
          "Dies bedeutet, dass zusätzliche Mikronährstoffe notwendig sind, um deinen Homocysteinspiegel zu senken.",
          "Das Ergebnis wäre, dass Homocystein dadurch abgebaut werden kann.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question: "Möchtest du eine Empfehlung basierend auf deiner Fähigkeit, Homosystein in deinem Körper abzubauen?",
        GOOD:
          "Sehr gut! Dein Homocysteinspiegel sollte in Ordnung sein. Wenn du dir trotzdem was Gutes tun willst, mache regelmäßig Sport und Bewegung und ernähre dich gesund!",
        BAD:
          "Bitte verzichte auf die Einnahme von Folsäure. Führe stattdessen bei einem Folsäuremangel 5-MTHF (bioaktive Folatform) zu. Weitere Nährstoffe um Homocystein zu senken, sind Vitamin B6 und Vitamin B12. Mache regelmäßig Ausdauersport, reduziere bei Bedarf Übergewicht, iss viel grünes Gemüse und reduziere tierische Proteine.  Verzichte auf Nikotin und andere Umweltgifte. ",
      },
    },
    VITB2HOMO_INFORMATION: {
      header: "Vitamin B2",
      [FeedbackType.STORYDATA]: {
        question:
          "Vitamin B2 kann deinem Körper helfen, Homocystein abzubauen, abhängig von deinen Genen. Möchtest du wissen, ob Vitamin B2 dir helfen kann, deinen Homocysteinspiegel zu senken?",
        description:
          "Vitamin B2 ist nicht nur essentiell für die Energieversorgung der Zellen, sondern unterstützt auch das MTHFR-Gen beim Abbau von Homocystein. Erhöhte Homocysteinwerte stehen in Zusammenhang mit einer Vielzahl von gesundheitlichen Problemen. In Kombination mit den richtigen Genen ist Vitamin B2 in der Lage, diesen Wert zu senken.",
        GOOD:
          "Gut zu wissen! Durch deine genetische Veranlagung hat Vitamin B2 hat eine senkende Wirkung auf deinen Homocysteinspiegel in deinem Körper.",
        GOOD_list: [
          "Vitamin B2 kann in der Ernährung ergänzt werden.",
          "Dies führt dazu, dass Homocystein in deinem Körper reduziert wird.",
          "Das Ergebnis ist ein niedrigerer Homocysteinspiegel.",
        ],
        BAD:
          "Aufgrund deiner genetischen Veranlagung hat Vitamin B2 keine senkende Wirkung auf deinen Homocysteinspiegel.",
        BAD_list: [
          "Vitamin B2 kann in der Ernährung ergänzt werden.",
          "Es hat jedoch keinen Einfluss auf deinen Homocysteinspiegel.",
          "Dies bedeutet, dass dein Homocysteinspiegel dadurch nicht reduziert werden kann.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question:
          "Möchtest du eine Empfehlung auf Basis der Wirksamkeit von Vitamin B2 zur Senkung des Homocysteinspiegels im Körper?",
        GOOD:
          "Um den Homocysteinspiegel zu senken solltest du Vitamin B2 als Nährstoff ergänzen. Verzichte bei einem erhöhten Homocysteinspiegel bitte unbedingt auf Nikotin und reduziere deinen Alkoholkonsum. Achte auf ein gutes Stressmanagement und übertreib es nicht mit dem Kaffeekonsum.",
        BAD:
          "Anstatt Vitamin B2 kannst du B6, B12 und Folat zur Homocysteinsenkung ergänzen. Verzichte bei einem erhöhten Homocysteinspiegel bitte unbedingt auf Nikotin und reduziere deinen Alkoholkonsum. Achte auf ein gutes Stressmanagement und übertreib es nicht mit dem Kaffeekonsum.",
      },
    },
    SALTHYP_INFORMATION: {
      header: "Salz",
      [FeedbackType.STORYDATA]: {
        question:
          "Zu viel Salz in deiner Ernährung kann zu einem erhöhten Blutdruck führen. Abhängig von deinen Genen kann die Reduktion von Salz besonders wirksam bei der Senkung deines Blutdrucks sein. Möchtest du wissen, ob dies auf dich zutrifft?",
        description:
          "Salz ist lebenswichtig und muss dem Körper jeden Tag über die Nahrung zugeführt werden. Zu viel Salz im Essen kann jedoch zu erhöhtem Blutdruck führen. Je mehr Salz im Körper ist, desto mehr Flüssigkeit benötigt der Körper. Steigt die Salzaufnahme, wird das benötigte Wasser den Zellen entzogen. Um das Salz möglichst schnell über die Nieren aus dem Blut zu spülen, erhöht der Körper den Blutdruck. Dieser Effekt ist bei bestimmten genetischen Typen besonders stark. Daher kann es sinnvoll sein, die Salzaufnahme durch die Nahrung zu reduzieren.",
        GOOD:
          "Gut zu wissen! Eine verringerte Salzaufnahme ist besonders wirksam bei der Senkung des Blutdrucks und der Vorbeugung von Bluthochdruck.",
        GOOD_list: [
          "Salz kann in der Nahrung aufgenommen werden.",
          "Deine Gene zeigen, dass die Reduzierung von Salz besonders effektiv ist.",
          "Eine erhöhte Salzaufnahme beeinflusst jedoch deinen Blutdruck nicht übermäßig.",
        ],
        BAD:
          "Aufgrund deiner genetischen Veranlagung ist eine verringerte Salzaufnahme mäßig wirksam bei der Senkung deines Blutdrucks und der Vorbeugung von Bluthochdruck.",
        BAD_list: [
          "Salz kann mit der Nahrung aufgenommen werden.",
          "Deine Genvariation zeigt, dass die Reduzierung von Salz mäßig wirksam ist.",
          "Zu viel Salz kann jedoch den Blutdruck stark erhöhen!",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question: "Möchtest du eine Empfehlung, ob eine Salzreduktion dabei helfen würde deinen Blutdruck zu senken?",
        GOOD:
          "Solltest du an erhöhtem Blutdruck leiden, kann sich eine Salzreduktion sehr positiv auswirken, sprich deinen Blutdruck senken.",
        BAD: "Eine Salzreduktion tut dir gut, hat aber nur eine mäßige Wirkung auf die Senkung deines Blutdrucks.",
      },
    },
    CAFFEINE_INFORMATION: {
      header: "Koffein",
      [FeedbackType.STORYDATA]: {
        question:
          "Kaffee enthält gesunde Antioxidantien, aber auch ungesundes Koffein, das deine Gesundheit beeinträchtigen kann. Deine Gene bestimmen, ob du Koffein ausreichend neutralisieren kannst. Möchtest du wissen, ob du Koffein ausreichend neutralisieren kannst?",
        description:
          "Kaffee zählt seit Jahrhunderten zu den beliebtesten Getränken weltweit. Zahlreiche Studien haben bereits gezeigt, dass moderater Kaffeekonsum viele positive Auswirkungen auf die Gesundheit haben kann. Dies wird jedoch auch von der Genetik bestimmt. Kaffee enthält viele verschiedene Inhaltsstoffe. Zum Beispiel kann er unseren Körper mit hohen Mengen an Antioxidantien versorgen, die die Zellen vor schädlichen Radikalen schützen. Außerdem enthält er ungesundes Koffein, das im Körper jedoch durch das CYP1A2-Gen abgebaut wird. Eine häufig vorkommende genetische Variation kann diesen Abbau verlangsamen, was die Gesundheit beeinträchtigen kann.",
        GOOD:
          "Lust auf einen Kaffee – kein Problem! Koffein wird in deinem Körper normal abgebaut und gesunde Antioxidantien im Kaffee schützen vor freien Radikalen.",
        GOOD_list: [
          "In einer Tasse Kaffee sind sowohl gesunde Antioxidantien als auch ungesundes Koffein enthalten.",
          "Dein CYP1A2 - Gen baut Koffein normal ab.",
          "Dies bedeutet, dass Koffein in deinem Körper ausreichend neutralisiert wird.",
          "Das Ergebnis sind gesunde Antioxidantien, die vor ungesunden Stoffen schützen.",
        ],
        BAD:
          "Aufgrund deiner genetischen Veranlagung wird Koffein in deinem Körper nur sehr langsam abgebaut und kann deine Gesundheit beeinträchtigen, obwohl gesunde Antioxidantien im Kaffee vor freien Radikalen schützen.",
        BAD_list: [
          "In einer Tasse Kaffee sind sowohl gesunde Antioxidantien als auch ungesundes Koffein enthalten.",
          "Deine CYP1A2 - Genvariation bedeutet, dass Koffein nur langsam abgebaut wird.",
          "Das Ergebnis ist, dass Koffein nicht neutralisiert wird und sich negativ auf deine Gesundheit auswirken kann.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question: "Möchten Sie eine Empfehlung basierend auf deiner Fähigkeit Koffein abzubauen?",
        GOOD:
          "Du kannst Koffein normal abbauen und Kaffee deshalb in Maßen konsumieren. Achte bitte trotzdem auf einen gesunden Lebensstil.",
        BAD: "Du solltest auf Koffein besser ganz verzichten bzw. zumindest deinen Konsum stark einschränken.",
      },
    },
    Q10_INFORMATION: {
      header: "Coenzym Q10",
      [FeedbackType.STORYDATA]: {
        question:
          "Coenzym Q10 gilt als starkes Antioxidans und kann bei Aktivierung im Körper vor freien Radikalen schützen. Möchtest du wissen, ob Q10 in deinem Körper aktiviert wird und dich vor freien Radikalen schützt?",
        description:
          "Coenzym Q10 gilt als starkes Antioxidans. Es wird über die Nahrung aufgenommen und schützt vor freien Radikalen. Coenzym Q10 ist ein wichtiger Stoff, der den Körper vor giftigen Stoffen, sogenannten freien Radikalen, schützt. Q10 hat aber in seiner Ursprungsform keine Wirkung. Es muss im Körper erst in seine aktive Form umgewandelt werden, um diese freien Radikale zu neutralisieren. Ein bestimmtes Gen (NQO1) ist für diese Umwandlung verantwortlich.",
        GOOD:
          "Alles gut! Inaktives Coenzym Q10 kann in deinem Körper ausreichend aktiviert werden und bietet somit Schutz vor freien Radikalen.",
        GOOD_list: [
          "Das NQ01-Gen produziert das NQ01-Protein.",
          "Dies bedeutet, dass Q10 in seine aktive Form Ubichinol umgewandelt wird.",
          "Ubiquinol hilft, freie Radikale im Körper zu neutralisieren.",
        ],
        BAD:
          "Inaktives Coenzym Q10 (Ubiquinon) kann aufgrund deiner genetischen Veranlagung im Körper nicht ausreichend aktiviert werden und schützt daher nicht vor freien Radikalen. Da Q10 unwirksam bleibt, können aber andere Antioxidantien vor freien Radikalen schützen.",
        BAD_list: [
          "Das NQ01-Gen produziert keine NQO1-Proteine.",
          "Dies bedeutet, dass Q10 in deinem Körper nicht aktiviert wird.",
          "Andere Antioxidantien können zum Schutz vor freien Radikalen supplementiert werden.Alternativ kann auch aktiviertes Q10(Ubiquinol) verwendet werden.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question:
          "Möchtest du eine Empfehlung basierend auf der Wirksamkeit von Coenzym Q10 zur Bekämpfung freier Radikale in deinem Körper?",
        GOOD:
          "Dein Körper kann Q10 in seine aktive Form, Ubiquinol, umwandeln. Deshalb reicht es ein nichtaktiviertes Q10-Präparat einzunehmen.",
        BAD:
          "Bitte achte darauf, dass du die aktive Form vom Coenzym Q10 zuführst: Ubiquinol. Gönne dir zusätzlich wichtige andere Antioxidantien, wie z.B. Vitamin C und Selen. Mit reichlich frischem Obst und Gemüse kannst du deinen Körper zusätzlich unterstützen.",
      },
    },
    SELEN_INFORMATION: {
      header: "Selen",
      [FeedbackType.STORYDATA]: {
        question:
          "Selen kann deinem Körper helfen, schädliche freie Radikale zu neutralisieren. Jedoch benötigen manche Menschen größere Mengen davon. Möchtest du wissen, ob ein normaler Selengehalt für dich ausreicht?",
        description:
          "Das GPX1-Gen hilft beim Binden und Neutralisieren von freien Radikalen. Für diese Funktion benötigt das Gen eine ausreichende Menge an Selen. Durch eine genetische Variation kann jedoch Selen schlechter gebunden werden. Dadurch bietet das GPX1-Gen weniger Schutz vor freien Radikalen.",
        GOOD:
          "Gute Neuigkeiten! Dein GPX1-Gen funktioniert ordnungsgemäß und bietet daher einen guten Schutz gegen freie Radikale mit einer normalen Menge Selen.",
        GOOD_list: [
          "Deine Gene produzieren GPX1-Enzyme korrekt.",
          "Selen ist für die Produktion des GPX1 - Enzyms notwendig.",
          "Das GPX1 - Enzym bindet stark an freie Radikale und neutralisiert viele von ihnen.",
          "Dies bedeutet, dass freie Radikale neutralisiert werden.",
        ],
        BAD:
          "Aufgrund deiner genetischen Veranlagung funktioniert das GPX1-Gen nicht richtig und bietet daher keinen guten Schutz gegen freie Radikale. Da freie Radikale nicht ausreichend neutralisiert werden, wird eine größere Menge Selen benötigt.",
        BAD_list: [
          "Deine Gene produzieren GPX1-Enzyme, die leicht variieren.",
          "Selen ist für die Produktion des GPX1 - Enzyms notwendig.",
          "Aufgrund der Variation hat das GPX1 - Enzym eine schwächere Bindungsfähigkeit und kann daher weniger freie Radikale unschädlich machen.",
          "Dies bedeutet, dass freie Radikale nicht ausreichend neutralisiert werden.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question: "Möchtest du eine Empfehlung basierend darauf, ob dein GPX1-Gen effektiv arbeitet oder nicht?",
        GOOD:
          "Achte bitte auf eine ausreichende Selenzufuhr über die Nahrung. Paranüsse sind z.B. reich an Selen. Bei einem Mangel führe Selen als Nährstoff zu.",
        BAD:
          "Dein Selenbedarf ist erhöht. Bitte achte auf eine ausreichende und regelmäßige Zufuhr von Selen als Nahrungsergänzungsmittel. ",
      },
    },
    HEAV_INFORMATION: {
      header: "Schwermetalle",
      [FeedbackType.STORYDATA]: {
        question:
          "Schwermetalle wie Quecksilber und Blei werden vom Körper aus der Umwelt aufgenommen und können schädlich sein, wenn sie nicht vom Körper gebunden und neutralisiert werden. Möchtest du wissen, ob deine Schwermetallentgiftung optimal funktioniert?",
        description:
          "Schwermetalle wie Quecksilber, Blei und Cadmium sind fester Bestandteil der Umwelt und werden täglich von jedem Menschen über die Nahrung oder Haut aufgenommen. Daher ist es für den Körper wichtig, diese Schadstoffe zu binden und unschädlich zu machen. Diese Aufgabe wird von verschiedenen Entgiftungsgenen übernommen. Funktionieren diese Gene jedoch nicht richtig, können sie ihre Aufgabe nicht ausreichend erfüllen und die erhöhte Belastung durch Schwermetalle kann die Gesundheit gefährden.",
        GOOD:
          "Alles Gut! Aufgenommene Schwermetalle können in deinem Körper ausreichend gebunden und neutralisiert werden.",
        GOOD_list: [
          "Schwermetalle wie Blei, Cadmium und Quecksilber werden über die Nahrung und die Haut aufgenommen.",
          "Deine Entgiftungsgene erfüllen ihre Aufgabe ausreichend.",
          "Dies bedeutet, dass Schwermetalle erkannt und gebunden werden.",
          "Das Ergebnis ist, dass Schwermetalle ausgeschieden werden.",
        ],
        BAD:
          "Aufgrund deiner genetischen Veranlagung können absorbierte Schwermetalle im Körper nicht ausreichend gebunden und neutralisiert werden, was zu einer begrenzten Entgiftung der Schwermetalle führt.",
        BAD_list: [
          "Schwermetalle wie Blei, Cadmium und Quecksilber werden über die Nahrung und die Haut aufgenommen.",
          "Deine Entgiftungsgene können ihre Aufgabe nicht ausreichend erfüllen.",
          "Zusätzliche Mineralien aus der Nahrung binden Schwermetalle.",
          "Als Ergebnis werden Schwermetalle entfernt und der Schutz wieder hergestellt.",
        ],
      },
      [FeedbackType.STORYRECOMMENDATION]: {
        question:
          "Möchtest du eine Empfehlung basierend auf der Effektivität der Bindung und Neutralisierung von Schwermetallen in deinem Körper?",
        GOOD:
          "Du bist ein guter Entgifter. Achte bitte trotzdem darauf, deinen Körper nicht unnötig mit z.B. Zigarettenrauch und Amalgamplomben zu strapazieren.",
        BAD:
          "Du solltest die Entgiftungsleistung deines Körpers unterstützen, indem du ausreichend Wasser trinkst und dich regelmäßig bewegst. Zusätzlich empfehlen wir dir Nährstoffe, wie Selen und Zink, welche die Entgiftung unterstützen, zu ergänzen.",
      },
    },
    HEART_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "Wir analysieren 21 Genvariationen die einen Einfluss auf deine Herzgesundheit haben. Möchtest du die Ergebnisse dieser Gene sehen?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Ich kann dir Lebensmittel empfehlen, die du auf der Basis deiner Gene meiden oder mehr zu dir nehmen sollst, um dein Herz gesund zu halten. Möchtest du mehr erfahren?",
      },
    },
    BONE_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "Wir analysieren in deinem Digital Twin 4 Genvariationen, die deine Knochengesundheit beeinflussen. Möchtest du die Ergebnisse dieser Gene sehen?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Ich kann dir Empfehlungen zu Mikronährstoffe geben, um deine Knochengesundheit zu verbessern. Möchtest du mehr erfahren?",
      },
    },
    EYE_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "Wir analysieren in deinem Digital Twin 3 Genvariationen, die deine Augengesundheit beeinflussen. Möchtest du die Ergebnisse für diese Gene sehen?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Ich kann dir zeigen, welche Mikronährstoffe du vermeiden oder mehr zu dir nehmen solltest, um die Gesundheit deiner Augen zu optimieren. Möchtest du mehr erfahren?",
      },
    },
    BRAIN_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "Wir analysieren 3 verschiedene Gene, die die Gesundheit deines Gehirns beeinflussen. Möchtet du die Ergebnisse jetzt sehen?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Ich kann dir zeigen, welche Mikronährstoffe du vermeiden oder mehr zu dir nehmen solltest, um deine Gedächtnisleistung zu optimieren. Möchtest du mehr erfahren?",
      },
    },
    JOINT_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "Wir analysieren 3 Genvariationen, die die Gesundheit deiner Gelenke beeinflussen. Möchtest du dir diese Gene jetzt anzeigen lassen?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Ich kann dir zeigen, welche Mikronährstoffe du vermeiden oder mehr zu dir nehmen solltest, um die Gesundheit deiner Gelenke zu optimieren. Möchtest du mehr erfahren?",
      },
    },
    HOMOCYSTEINE_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "Wir analysieren in deinem Digital Twin derzeit 3 Genvariationen, die die Gesundheit deines Bluts beeinflussen. Möchtest du dir diese Gene jetzt anzeigen lassen?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Ich kann dir zeigen, welche Mikronährstoffe du vermeiden oder mehr zu dir nehmen solltest, um deine Blutwerte zu verbessern. Möchtest du mehr erfahren?",
      },
    },
    HOMOCYSTEINE_B2_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "Wir analysieren in deinem Digital Twin ein Gen, das die Verwertung von Vitamin B2 beeinflusst. Möchtest du dir dieses Gen jetzt anzeigen lassen?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Ich kann dir zeigen, ob du mehr oder weniger Vitamin B2 zu dir nehmen sollst. Möchtest du mehr erfahren?",
      },
    },
    HYPERTENSION_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "Ich kann dir drei Genvariationen zeigen, die deinen Blutdruck beeinflussen. Möchtest du diese Gene nun anzeigen lassen?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Ich kann dir zeigen, welche Mikronährstoffe du vermeiden oder mehr zu dir nehmen solltest, um deinen Blutdruck positiv zu beeinflussen. Möchtest du mehr erfahren?",
      },
    },
    METABOLISM_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "Ich habe für dich 8 Genvariationen analysiert, die die Funktion deines Stoffwechsels beeinflussen. Möchtest du die zuständigen Gene und die Ergebnisse nun anzeigen lassen?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Ich kann dir zeigen, welche Mikronährstoffe du vermeiden oder mehr zu dir nehmen solltest, um deinen Stoffwechsel positiv zu beeinflussen. Möchtest du mehr erfahren?",
      },
    },
    OXIDATIVESTRESS_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "Ich habe für dich 5 Genvariationen untersucht, die Oxidativen Stress in deinem Körper beeinflussen. Möchtest du dir die Ergebnisse nun anzeigen lassen und mehr über diese Gene erfahren?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Ich kann dir zeigen, welche Mikronährstoffe du vermeiden oder mehr zu dir nehmen solltest, um freie Radikale zu binden und Oxidativen Stress zu reduzieren. Möchtest du mehr erfahren?",
      },
    },
    DETOX_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "Ich habe für dich 11 Genvariationen untersucht, die beeinflussen, wie gut du Schwermetalle wie z.B. Quecksilver neutralisieren kannst. Möchtest du dir diese Gene und die Ergebnisse nun anzeigen lassen?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Ich kann dir zeigen, welche Mikronährstoffe du vermeiden oder mehr zu dir nehmen solltest, um Schwermetalle in deinem Körper zu neutralisieren. Möchtest du mehr erfahren?",
      },
    },
    GLUTEN_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "Ich habe zwei Genvariationen untersucht, die beeinflussen, wie gut du Gluten verwerten kannst. Möchtest du dir diese Gene und die Ergebnisse anzeigen lassen?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Möchtest du Empfehlungen für Lebensmittel und Mikronährstoffe, basierend auf deinen Genen? Hinweis: Wenn du sensitiv auf Gluten bist, empfehlen wir dir Gluten allgemein zu reduzieren.",
      },
      [FeedbackType.GENE_INFO]: {
        heading: "Gluten",
        description_1:
          "Gluten hilft Lebensmitteln, ihre Form zu behalten, indem es als Klebstoff fungiert, der sie zusammenhält. Außerdem sorgt es für Elastizität – sodass Brot aufgehen kann –  und verleiht glutenhaltigen Lebensmitteln eine zähe Struktur. Menschen mit einer Glutenunverträglichkeit sollten es jedoch meiden.",
        description_2:
          "Es wurden zwei Gene identifiziert, die beeinflussen, wie dein Körper auf Gluten in der Nahrung reagiert. Wenn diese beiden Gene zusammen analysiert werden, können sie Aufschluss darüber geben, ob du ein geringes bis gar kein Risiko, ein mäßiges Risiko oder ein hohes Risiko für eine Glutenunverträglichkeit hast und ob die Voraussetzung für eine Glutenunverträglichkeit erfüllt sind.",
        warning:
          "Ein erhöhtes genetisches Risiko bedeutet nicht automatisch, dass man eine Unverträglichkeit hat – umgekehrt gilt das genauso.",
        link: "Überprüfung des analysierten Gens",
      },
      [FeedbackType.GENE_RISK_INFO]: {
        heading: "Glutenunverträglichkeit",
        content_1:
          "Es gibt verschiedene Formen von Glutenunverträglichkeiten, z. B. Glutenunverträglichkeit, Weizenallergie oder Zöliakie. Wenn dein Arzt bei dir eine Unverträglichkeit diagnostiziert, solltest du je nach Schweregrad glutenhaltige Produkte sehr selten oder gar nicht verzehren. ",
        content_2:
          "Typische Anzeichen, die nach dem Verzehr von Getreideprodukten auftreten können, sind Bauchschmerzen, Blähungen, Übelkeit, Durchfall oder Verstopfung. Wenn du glaubst, dass du eine Unverträglichkeit hast, such einen Arzt auf.",
        subheading: "Was tun bei einer Unverträglichkeit?",
        subheading_content: `Natürlich ist es schwer, auf Pizza, Nudeln, Cornflakes, Puffreis oder Couscous zu verzichten. In den meisten Supermärkten gibt es jedoch bereits eine große Auswahl an Ersatzprodukten, wie glutenfreies Brot oder glutenfreie Nudeln, sodass du nicht auf viel verzichten musst. Diese Produkte werden oft aus anderen Mehlsorten hergestellt. Achte beim Lesen von Lebensmitteletiketten auf das Wort "GF" (glutenfrei) und den Buchstaben "A" für Allergene. Viele Lebensmittel, vor allem verarbeitete, enthalten Gluten in Zusatzstoffen wie Malzaroma und modifizierter Speisestärke.`,
        food_sources_header: "Glutenfreie Alternativen",
      },
    },
    LACTOSE_INFORMATION: {
      [FeedbackType.GENES]: {
        question:
          "Ich habe für dich ein Gen untersucht, dass deine Fähigkeit beeinflusst Laktose zu verwerten. Möchtest du dir dieses Gen und das Ergebnis anzeigen lassen?",
      },
      [FeedbackType.NUTRIENTS]: {
        question:
          "Möchtest du Empfehlungen für Lebensmittel und Mikronährstoffe, basierend auf deinen Genen? Hinweis: Wenn du sensitiv auf Laktose bist, empfehlen wir dir Laktose allgemein zu reduzieren.",
      },
      [FeedbackType.GENE_INFO]: {
        heading: "Laktose",
        description_1:
          "Laktose - auch Milchzucker genannt - ist in Milch und Milchprodukten wie Käse, Joghurt und Butter, aber auch in vielen Fertiggerichten enthalten.",
        description_2:
          "Es gibt ein Gen, das sich darauf auswirkt, ob dein Körper genug von dem Enzym Laktase herstellt, das für den Abbau von Laktose im Darm benötigt wird. Dieses Gen kann dir Auskunft darüber geben, ob du ein geringes oder ein sehr hohes Risiko für eine Laktoseintoleranz hast.",
        warning:
          "Ein erhöhtes genetisches Risiko bedeutet nicht automatisch, dass man eine Unverträglichkeit hat – umgekehrt gilt das genauso.",
        link: "Überprüfung des analysierten Gens",
      },
      [FeedbackType.GENE_RISK_INFO]: {
        heading: "Laktoseintoleranz",
        content_1:
          "Bei einer Laktoseintoleranz wird der Milchzucker im Dünndarm nicht aufgespalten und kann daher zu einem unerwünschten Gärungsprozess im Dickdarm führen.",
        content_2:
          "Typische Anzeichen sind Bauchschmerzen, Blähungen, Übelkeit oder Durchfall ein bis zwei Stunden nach dem Verzehr von Milchprodukten. Wenn du glaubst, dass du an einer Unverträglichkeit leidest, solltest du einen Arzt aufsuchen, um dies zu überprüfen.",
        subheading: "Was tun bei einer Unverträglichkeit?",
        subheading_content: `Laktose kann nicht nur in klassischen Milchprodukten enthalten sein, sondern auch in Kartoffelpüree, Eiscreme, Soßen, Wurstwaren und Medikamenten. Je nach Schweregrad der Unverträglichkeit solltest du dir die Inhaltsstoffe der Produkte immer genau ansehen und bei der Auflistung der Allergene auf den Buchstaben "G" achten. Die meisten Supermärkte haben bereits eine große Auswahl an laktosefreien Ersatzprodukten.`,
        food_sources_header: `Laktosefreie Alternativen`,
      },
    },
    TOTAL_INFORMATION: {
      [FeedbackType.NUTRIENTS]: {
        question:
          "Ich kann dir dabei helfen die Zufuhr von wichtigen Mikronährstoffen zu optimieren. Möchtest du mehr dazu erfahren und dir deine Liste anzeigen lassen?",
      },
    },
    GENES_INTRO: {
      [FeedbackType.INTRODUCTION]: {
        question:
          "Deine Gene können viel über dich erzählen, beispielsweise welchen Einfluss Ernährung und Lebensstil auf deine Gesundheit haben. Möchtest du mehr über die Funktion deiner Gene erfahren?",
        description:
          "In der DNA des Menschen kommen insgesamt vier verschiedene Nukleinbasen vor: Adenin (A), Thymin (T), Guanin (G) und Cytosin (C). Nukleinbasen ist die Bezeichnung der Bausteine, aus denen deine DNA zusammengesetzt ist. Diese vier Basen der DNA kodieren durch ihre Abfolge die Erbinformationen. Deshalb werden die vier Buchstaben A, C, G, und T auch als Alphabet des Lebens bezeichnet. Ein Genotyp bezieht sich auf das Paar von SNPs, die an einer bestimmten chromosomalen Position vererbt wurden, jeweils eines mütterlicherseits und eines väterlicherseits. ",
      },
    },
    GENESTORY_INTRO: {
      [FeedbackType.INTRODUCTION]: {
        question:
          'Die Ergebnisse deiner Genanalyse werden in sogenannten "Gene stories" präsentiert. Möchtest du mehr darüber erfahren?',
        description:
          "Zunächst werden verschiedene genetische Variationen untersucht, die alle Einfluss auf verschiedene Bereiche der Gesundheit haben. Zum Beispiel haben Gene aus der Kategorie Herz, Einfluss auf die Herzgesundheit und damit verbundenen Risiken (erhöhtes Cholesterin, erhöhte Triglyceridewerte, etc.). Abhängig von diesen individuellen genetischen Eigenschaften, werden in der zugehörigen Auswertung die Mikronährstoffe aufgelistet, die positive bzw. negative Einfluss auf diesen Bereich der Gesundheit haben und anhand deiner individuellen genetischen Stärken und Schwächen bewertet. Ist das Risiko hoch werden Mikronährstoffe die nachweislich präventive Wirkung haben erhöht und Stoffe, die das Risiko weiter erhöhen reduziert.",
      },
    },
    RECOMMENDATION_INTRO: {
      [FeedbackType.INTRODUCTION]: {
        question:
          'Basierend auf deinen persönlichen "Gene stories" kann ich dir Empfehlungen geben, wie du deinen Lebensstil optimieren kannst. Möchtest du mehr dazu erfahren?',
        description:
          "In enger Zusammenarbeit mit unseren ErnährungsexpertInnen überprüfen wir jedes genetische Profil und geben angepasst persönliche Empfehlungen, die du schon heute umsetzen kannst, um deine Gesundheit basierend auf deinen einzigartigen genetischen Merkmalen zu optimieren.",
      },
    },
    NUTRIENTS_INTRO: {
      [FeedbackType.INTRODUCTION]: {
        question:
          "Ich kann dir dabei helfen ausgewählte Nährstoffe optimal an deine Gene anzupassen, d.h. zu reduzieren oder zu erhöhen. Möchtest du mehr darüber erfahren, wie du zu diesen Empfehlungen kommst?",
        description:
          "Mikronährstoffe (Vitamine, Mineralstoffe und Spurenelemente) sind für viele unserer Körperfunktionen essenziell und lebensnotwendig. Einige dieser Stoffe können vom Körper selbst produziert werden, andere müssen über die Nahrung aufgenommen werden. Da Mängel zu ernsthaften Gesundheitsproblemen führen können, ist es wichtig diese Stoffe in ausreichender Menge zuzuführen. Da jedoch nicht jeder Mensch die gleichen genetischen Voraussetzungen hat, ist auch der Bedarf an Mikronährstoffen unterschiedlich. Eine Analyse der genetischen Stärken und Schwächen kann also dabei helfen, die richtigen Mikronährstoffe in der richtigen Menge zuzuführen und besonders negative Stoffe zu meiden. Jeder Mensch hat zum Beispiel unterschiedliche genetische Risiken, die durch die richtige Ernährung bzw. die Erhöhung und Reduktion verschiedener Mikronährstoffe, minimiert werden können. Außerdem gibt es Stoffe die aufgrund genetischer Variationen nicht richtig umgewandelt und aktiviert werden können. Auch in diesem Fall hilft dieses Wissen, um die richtigen Mikronährstoffe und damit Lebensmittel auszuwählen.",
      },
    },
    INTOLERANCE_INTRO: {
      [FeedbackType.INTRODUCTION]: {
        question:
          "Ich kann dir auch dabei helfen zu verstehen, ob du Milch oder z.B. Getreide gut verarbeiten kannst. Möchtest du mehr darüber erfahren?",
        description:
          "Eine DNA Analyse kann keine Unverträglichkeiten feststellen. Es geht vielmehr darum, genetische Schwächen (wie Veranlagung zu schlechten Blutwerten etc.) festzustellen und mit optimierter Ernährung präventiv dagegen zu wirken. Wenn Unverträglichkeiten bestehen, sollten diese Lebensmittel unabhängig vom DNA Ergebnis gemieden werden.",
      },
    },
    NUTRIENT_OVERVIEW_INTRO: {
      [FeedbackType.INTRODUCTION]: {
        question:
          "Ich kann dir dabei helfen die Zufuhr von wichtigen Mikronährstoffen zu optimieren. Möchtest du mehr dazu erfahren und dir deine Liste anzeigen lassen?",
        description: "LINK TO NUTRIENT OVERIEW",
      },
    },
    RECOMMENDED_DAILY_MICRO: {
      [FeedbackType.MICRONUTRIENTS]: {
        question:
          "Ich kann dir auf der Basis deiner individuellen Genstruktur die optimale Menge an Mikronährstoffen für den täglichen Bedarf anzeigen. Möchtest du mehr erfahren?",
        description: "LINK TO PERSONALISED DAILY ALLOWANCES",
      },
    },
  },
  shop_button: {
    heading: "Personalisiere deinen Zwilling!",
    content:
      "In unserem Online-Shop erhältst du <0>dein DNA-Kit</0>, mit dem wir deine genetischen Veranlagungen auswerten können und dein persönlicher, digitaler Zwilling erstellt wird.",
    button: "Auf zum Shop",
  },
  user_data: {
    header: "Deine Daten",
    info: {
      account_info: {
        header: "Dein Konto",
        email: "E-Mail-Adresse",
        change_password_button: "Passwort ändern",
      },
      digital_twin_info: {
        header: "Dein Digital Twin",
        firstname: {
          label: "Vorname",
        },
        lastname: {
          label: "Nachname",
        },
        gender: {
          label: "Geschlecht",
        },
        weight: {
          label: "Gewicht (kg)",
        },
        date_of_birth: {
          label: "Geburtstag",
        },
        height: {
          label: "Größe (cm)",
        },
        ethnicity: {
          label: "Ethnische Zugehörigkeit",
        },
        diet_preference: {
          label: "Ernährungspräferenz",
        },
      },
      save_button: "Änderungen speichern",
    },
    change_password: {
      header: "Passwort ändern",
      subtitle:
        "Verwende zum Schutz deiner Daten sichere Passwörter mit mehr als 8 Zeichen, Groß-und Kleinbuchstaben, Zahlen oder Sonderzeichen.",
      old_password: "Altes Passwort",
      new_password: "Neues Passwort",
      confirm_new_password: "Neues Passwort wiederholen",
      button: "Neues Passwort speichern",
      messages: {
        password_updated: "Passwort wurde erfolgreich geändert.",
        invalid_old_password: "Das alte Passwort ist falsch.",
      },
    },
    messages: {
      information_updated: "Änderungen wurden erfolgreich gespeichert.",
    },
  },
  favourites: {
    header: "Deine Favoriten",
    subheader: "Hier kannst du die Seiten, die du in deinen Ergebnissen markiert hast, verwalten.",
    info: {
      [MenuItemSubcategory.OVERVIEW]: { 1: "Willkommen zu deinen Testergebnissen!" },
      [MenuItemSubcategory.MICRONUTRIENTS_TO_INCREASE]: { 1: "Mikronährstoffe +", 2: "Mikronährstoffe -" },
      [MenuItemSubcategory.FINDINGS]: { 1: "Info" },
      [MenuItemSubcategory.GENEVARIATIONS]: { 1: "Deine Genvariationen", 2: "Deine Genvariationen" },
      [MenuItemSubcategory.HEART]: {
        1: "Mikronährstoffe +",
        2: "Mikronährstoffe -",
        3: "Triglyceride",
        4: "Cholesterin",
        5: "Omega-3-Fettsäuren",
      },
      [MenuItemSubcategory.BRAIN]: {
        1: "Mikronährstoffe +",
        2: "Mikronährstoffe -",
        3: "Freie Radikale",
      },
      [MenuItemSubcategory.BONE]: {
        1: "Mikronährstoffe +",
        2: "Mikronährstoffe -",
        3: "Kalzium",
        4: "Vitamin D",
      },
      [MenuItemSubcategory.JOINT]: {
        1: "Mikronährstoffe +",
        2: "Mikronährstoffe -",
      },
      [MenuItemSubcategory.EYE]: {
        1: "Mikronährstoffe +",
        2: "Mikronährstoffe -",
      },
      [MenuItemSubcategory.OXIDATIVESTRESS]: {
        1: "Mikronährstoffe +",
        2: "Mikronährstoffe -",
        3: "Freie Radikale",
        4: "Coenzyme Q10",
        5: "Selen",
      },
      [MenuItemSubcategory.METABOLISM]: {
        1: "Mikronährstoffe +",
        2: "Mikronährstoffe -",
      },
      [MenuItemSubcategory.DETOX]: {
        1: "Mikronährstoffe +",
        2: "Mikronährstoffe -",
        3: "Schwermetalle",
        4: "Koffein",
      },
      [MenuItemSubcategory.GLUTEN]: {
        1: "Gluten",
        2: "Glutenunverträglichkeit",
        3: "Mikronährstoffe +",
        4: "Mikronährstoffe -",
      },
      [MenuItemSubcategory.LACTOSE]: {
        1: "Laktose",
        2: "Laktoseintoleranz",
        3: "Mikronährstoffe +",
        4: "Mikronährstoffe -",
      },
      [MenuItemSubcategory.HOMOCYSTEINE]: {
        1: "Mikronährstoffe +",
        2: "Mikronährstoffe -",
        3: "Homocysteine",
        4: "Vitamin B2",
      },
      [MenuItemSubcategory.HYPERTENSION]: {
        1: "Mikronährstoffe +",
        2: "Mikronährstoffe -",
        3: "Salz",
      },
      [MenuItemSubcategory.TOTAL]: { 1: "Info" },
      [MenuItemSubcategory.MICRONUTRIENTS]: { 1: "Dein empfohlener Tagesbedarf" },
      [MenuItemSubcategory.RDA]: { 1: "Dein empfohlener Tagesbedarf" },
      [MenuItemSubcategory.FRUCTOSE]: { 1: "Info" },
      [MenuItemSubcategory.USER_DATA]: { 1: "Info" },
      [MenuItemSubcategory.FAVOURITES]: { 1: "Info" },
      [MenuItemSubcategory.RESILIENCE]: { 1: "Resilienz" },
    },
  },
  user_reports: {
    header: "Deine Ergebnisse",
    subheader: "Folgende Gen- und Blutanalysen wurden bisher für dich vorgenommen:",
    gene_analysis: {
      title: "Genanalyse",
      result: "Ernährung",
    },
    blood_analysis: {
      title: "Blutanalysen",
      button: "Blutanalyse-Kit bestellen",
    },
    tip: {
      title: "Messe regelmäßig deinen Fortschritt",
      content:
        "Wir empfehlen, alle zwei Monate ein Blutanalyse-Kit zu verwenden, um zu überprüfen, wie sich deine Werte verändern.",
    },
  },
  nutrient_modal: {
    food_sources: "Food sources:",
    food_source_sub_header: "Reihenfolge nach Konzentration (1 = höchste)",
    references: "Quellenangaben",
  },
  snackbar: {
    // Todo: translate
    get_nutrient_info_error: "Could not fetch data from backend",
  },
  mental_health: {
    resilience: {
      warning:
        "Eine genetische Veranlagung bedeutet nicht, dass du keine Techniken zur Überwindung oder Verbesserung deiner Resilienz anwenden kannst.",
      stress: {
        title: "Leistung in Stresssituationen",
        descriptions: {
          [ResilienceLevel.HIGH]:
            "Wie 41 % der Bevölkerung sind Sie eher in der Lage, in Stresssituationen bessere Leistungen zu erbringen, und verfügen über eine größere emotionale Belastbarkeit.",
          [ResilienceLevel.NORMAL]: "",
          [ResilienceLevel.LOW]: "",
          [ResilienceLevel.NONE]: "",
        },
      },
      emotion: {
        title: "Emotionale Belastbarkeit",
        descriptions: {
          [ResilienceLevel.HIGH]: "",
          [ResilienceLevel.NORMAL]:
            "Wie 44 % der Bevölkerung verfügen Sie über ein normales Maß an Belastbarkeit im Umgang mit stressigen Situationen und negativen Gefühlen.",
          [ResilienceLevel.LOW]: "",
          [ResilienceLevel.NONE]: "",
        },
      },
      impulsiveness: {
        title: "Impulsives Verhalten",
        descriptions: {
          [ResilienceLevel.HIGH]: "",
          [ResilienceLevel.NORMAL]: "",
          [ResilienceLevel.LOW]: "Wie 15 % der Bevölkerung neigst du eher dazu, impulsiv zu handeln.",
          [ResilienceLevel.NONE]: "",
        },
      },
    },
  },
};
